/**
 * `LocalRepository` — offline / dev-parity implementation of `Repository`
 * (docs/ARCHITECTURE.md §5.6). Selected with `VITE_REPOSITORY=local`
 * (`npm run dev:local`).
 *
 *  - The whole database is one `localStorage` key, `qd_local_v1`, whose payload
 *    is deliberately shaped like the prototype's `gt_v1`
 *    (`{users, list, session, pts, tiers, skips, tierOrder, potentials,
 *    intOrder}`) plus the fields the prototype kept in separate keys
 *    (`gt_fix_v1` → `facts`, `gt_assets_v1` → `assets`) and the ones it faked
 *    (`creds`, `steam`, `mutes`, `events`). INVENTORY §5.3 is the reference.
 *  - Seeded from `data/seed.ts` on first run, exactly as `boot()` did: saved
 *    data wins entirely once `users` is non-empty.
 *  - 0–40 ms artificial latency on every method, so optimistic-UI bugs surface
 *    locally rather than in production.
 *  - `BroadcastChannel('qd')` emulates Realtime: a write in tab A notifies
 *    tab B's `subscribeCrew` handler, which re-reads the store. That makes the
 *    two-tab reconciliation path testable with no backend at all.
 *  - Steam methods hit `/api/steam/*` for real; when the Worker is down they
 *    fall back to `data/fixtures/steam.ts` and report `fromNetwork: false`, so
 *    the freshness chip can flip to RETRYING (ARCHITECTURE §5.4) without the
 *    UI losing data.
 *
 * DEVIATIONS
 *  - L-01  Channel name is `'qd'` (the Phase 2b brief), not §5.6's `'qd_local'`.
 *  - L-02  Passwords are compared in plaintext against a seeded map. This is
 *          dev parity only and is the one place the production security model
 *          (Supabase Auth) is *not* modelled. It never ships: the factory in
 *          `repo/index.ts` only builds this class when `VITE_REPOSITORY=local`.
 *  - L-03  Sign-in succeeds for a `must_reset` user and returns
 *          `mustReset: true`, matching ARCHITECTURE §5.2 step 2 (session first,
 *          then the reset gate). The prototype instead refused to set
 *          `state.session` and jumped straight to the reset screen; the visible
 *          behaviour is identical.
 */

import {
  FIXTURE_ASSETS,
  FIXTURE_FACTS,
  FIXTURE_SEATS,
  FIXTURE_TAGS,
  fixtureBrowseFor,
  fixtureLiveFor,
} from '../data/fixtures/steam.ts';
import {
  localEntryId,
  ptSeed,
  seedBacklog,
  seedCatalog,
  seedCredentials,
  seedList,
  seedScores,
  seedSends,
  seedWatch,
  seedEvents,
  type SeedEvent,
  seedUsers,
  SEED_POTENTIALS,
  SEED_TIERS,
  SEED_TIER_ORDER,
} from '../data/seed.ts';
import { INVITE_TTL_DAYS } from '../lib/crewSlug.ts';
import { genPw, INVITE_BASE, inviteFor, ptFmt, ptPartyLbl } from '../lib/format.ts';
import {
  deriveUserKey,
  GOTY_STEAM_FILL_APPIDS,
  nextHue,
  pass,
  passFlt,
  passXTags,
  ptCheckFound,
  ptCheckUrl,
  ptTrackFields,
  PT_BROWSE,
  PT_CHECK_UNAVAILABLE,
  sanitizeVotes,
  sortArr,
  CHART_DAYS,
  PRICE_CHART_DAYS,
} from '../lib/logic.ts';
import type { RecoveryLink } from '../lib/joinLink.ts';
import { GOTY_YEAR } from '../lib/logic.ts';
import type { CcuSample, PriceSample } from '../lib/logic.ts';
import type { DetailFacts,
  MemberGotyYear,
} from './Repository.ts';
import { groupNoms, R_QUOTA, R_SPIN_MS, rGeom, rTier, ticketsFor } from '../lib/round.ts';
import type { RoundGeomRow, RoundSnapshot } from '../lib/round.ts';
import { scParse } from '../lib/schedule.ts';
import type { SchedMap, SchedNom } from '../lib/schedule.ts';
import type { VideoDraft } from '../lib/videoUrl.ts';
import type {
  AssetMap,
  AuthSession,
  BacklogEntry,
  BoardEvent,
  Comment,
  CrewEvent,
  CrewScoreRow,
  EntryId,
  Game,
  GameId,
  GameVideo,
  Invite,
  ListEntry,
  Playtest,
  Profile,
  PtBrowseRow,
  PtCheckData,
  PtCheckResult,
  PtOffInput,
  PtSteamInput,
  Role,
  RosterEntry,
  SteamFactPatch,
  ScoreRec,
  SendRec,
  SendStatus,
  WatchRec,
  SteamLink,
  SyncResult,
  Tier,
  Unsub,
  User,
  VoteValue,
} from '../lib/types.ts';
import type {
  AssetsResult,
  BrowsePageResult,
  BrowseQuery,
  SeatCount,
  CrewHandlers,
  CrewLinkRow,
  CrewOwnershipRow,
  CreateCrewInput,
  CrewMembership,
  CrewSnapshot,
  PlayerOverride,
  ResolvedPlayers,
  DiscoverPageResult,
  DiscoverQuery,
  EnrichResult,
  LiveSearchResult,
  MyBoards,
  PersonalHandlers,
  ProvisionalGame,
  Repository,
  SteamBackfill,
  SteamFillResult,
  SteamTag,
  SteamWishlistResult,
  InviteAccepted,
  InvitePeek,
  InviteSent,
  JoinAsked,
  JoinCodeSent,
  JoinLinkMinted,
  JoinLinkPeek,
  JoinLinkState,
  JoinRequest,
  JoinStatus,
  PendingInvite,
} from './Repository.ts';
import { RepoError } from './Repository.ts';
import {
  browseParams,
  emptyMedia,
  mapAppDetails,
  mapAppMedia,
  normName,
  steamApi,
  toBrowseGame,
  toLiveGame,
} from './steamApi.ts';

// ---------------------------------------------------------------------------
// injectable environment (so tests need neither jsdom nor a real BroadcastChannel)
// ---------------------------------------------------------------------------

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ChannelLike {
  postMessage(msg: unknown): void;
  addEventListener(type: 'message', cb: (ev: { data: unknown }) => void): void;
  removeEventListener(type: 'message', cb: (ev: { data: unknown }) => void): void;
  close(): void;
}

export interface LocalRepositoryOptions {
  storage?: StorageLike;
  /** Factory so a test can hand-roll a channel; defaults to `BroadcastChannel`. */
  channel?: () => ChannelLike | null;
  /** Max artificial latency in ms. Tests pass 0. */
  latencyMs?: number;
  now?: () => number;
  /** Base for `/api/steam/*`. Empty string = same origin (the Vite proxy). */
  apiBase?: string;
  fetchImpl?: typeof fetch;
}

export const LOCAL_DB_KEY = 'qd_local_v1';
export const LOCAL_CHANNEL = 'qd';
export const LOCAL_CREW_ID = 'local-crew';

interface LocalDb {
  v: 1;
  users: User[];
  creds: Record<string, string>;
  list: ListEntry[];
  pts: Playtest[];
  /** crew-submitted carousel links — the local mirror of `game_videos` */
  videos: GameVideo[];
  session: string;
  potentials: Record<string, GameId[]>;
  tiers: Record<string, Record<string, Tier>>;
  tierOrder: Record<string, GameId[]>;
  skips: Record<string, GameId[]>;
  intOrder: Record<string, GameId[]>;
  /** MY INTERESTS skip queue — the Supabase twin is `interest_order.skip_pos` (3037). */
  intSkips: Record<string, GameId[]>;
  /** MY GAMES > BACKLOG, per user key — the local twin of `backlog` (3060). */
  backlog: Record<string, BacklogEntry[]>;
  /** 0–10 scores, per user key then game — the local twin of `game_scores` (3060). */
  scores: Record<string, Record<string, ScoreRec>>;
  /** 3062 — the party's recommendations, every row (the local db is one crew) */
  sends: SendRec[];
  /** 3063 — my watches, by user key then game id */
  watch: Record<string, Record<string, WatchRec>>;
  /** 3063 — every NOTICE BOARD row, with the account it was written for */
  boardEvents: SeedEvent[];
  /** per-user playtest mutes — ARCHITECTURE §1.7 / PORTING-NOTES D-04 */
  mutes: Record<string, string[]>;
  steam: Record<string, SteamLink>;
  events: CrewEvent[];
  /** the crew-shared successor to `gt_fix_v1` */
  facts: Record<string, SteamFactPatch>;
  /** the successor to `gt_assets_v1` */
  assets: AssetMap;
  /** provisional catalog rows minted by `ensureGame` */
  extraGames: Game[];
  /**
   * Calls that outlived their list entry, by appid — the local stand-in for
   * what migration 3004 gets structurally.
   *
   * The server has no table like this: it re-keyed `votes` to
   * (crew_id, game_appid, user_id) so a call simply never dies with the entry.
   * This repo cannot copy that, because its whole model is votes embedded in
   * the entry object — so removal stashes the bag here and re-adding takes it
   * back out. Behaviourally identical, structurally different, and that
   * difference is the reason this comment exists: do not "fix" one side to
   * match the other without reading the other.
   */
  remembered: Record<string, Record<string, VoteValue>>;
  /**
   * Multi-crew, local mirror (phase C).
   *
   * WHAT THIS MODELS: which crews exist, who belongs to which with what role
   * and colour, and which one is active. That is enough for the switcher, the
   * crew slice, `myCrews`/`setActiveCrew`/`createCrew`/`deleteCrew`, and the
   * snapshot's crew identity.
   *
   * WHAT IT DOES NOT MODEL, deliberately and worth knowing before phase D
   * tests against it: the BOARD data above (`list`, `pts`, `potentials`,
   * `tiers`, `intOrder`, …) is still crew-agnostic, so switching crews in
   * local mode shows the same board rather than an empty one. On the server
   * that isolation comes from RLS and the 3016 rekey, neither of which has a
   * local equivalent. Making it real means keying every one of those maps by
   * crew — a bigger change than phase C needs, and phase D's two-crew walk is
   * specified against STAGING for exactly this reason (§7 of the plan).
   */
  crews: LocalCrew[];
  memberships: LocalMembership[];
  activeCrewId: string;
  /**
   * The open round (3039), or null. One per crew server-side; here one per
   * DB, like every other board field — local mode is not crew-isolated.
   * Same shape the server's `round_json()` returns, so the store reads both
   * repositories identically; the only local-only fact is that the draw uses
   * `Math.random()` here where production draws in `round_spin()`.
   */
  round: RoundSnapshot | null;
  /**
   * SCHEDULE nights (3071), keyed `YYYY-MM-DD`. Same shape `schedule_get()`
   * returns, so the store reads both repositories identically; like the round,
   * one per DB rather than per crew. An empty night is deleted, never `[]`.
   */
  sched: SchedMap;
  /**
   * Open and closed invitations (3026, docs/INVITES.md).
   *
   * The TOKEN is stored in the clear here, which the server deliberately does
   * not do — it keeps only a hash. That is not a shortcut being smuggled in:
   * local mode has no mail, so `peekInvite` has to be able to find a row from
   * the token the caller pastes back, and there is nothing to protect in a
   * store that already holds every password in `creds`.
   */
  invites: LocalInvite[];
  /** Join links, challenges and requests (3056, docs/JOIN-LINKS.md). Same
   *  clear-token reasoning as `invites`. */
  joinLinks: LocalJoinLink[];
  joinChallenges: LocalJoinChallenge[];
  joinRequests: LocalJoinRequest[];
  /**
   * The one outstanding password-reset link, or null (docs/EMAIL.md).
   *
   * ONE, not a list, and that is a modelling choice worth naming: GoTrue keeps
   * a single live recovery token per user, so asking again supersedes the last
   * link rather than adding a second. A local mode holding several would let a
   * surface be built on a promise production does not make.
   */
  recovery: LocalRecovery | null;
}

/** The local stand-in for GoTrue's recovery token. */
interface LocalRecovery {
  token: string;
  userKey: string;
}

/** The local stand-in for a `crew_invites` row. */
interface LocalInvite {
  token: string;
  crewId: string;
  email: string;
  invitedBy: string;
  createdAt: number;
  expiresAt: number;
  closedAs: '' | 'accepted' | 'declined' | 'revoked' | 'superseded';
}

/** The local stand-in for a `crew_join_links` row (3056). One per party, which
 *  is a primary key server-side and a find-by-crewId here. */
interface LocalJoinLink {
  crewId: string;
  token: string;
  createdAt: number;
  revoked: boolean;
}

/** The local stand-in for a `crew_join_challenges` row.
 *
 *  THE CODE IS IN THE CLEAR, for the same reason `LocalInvite.token` is: local
 *  mode has no mail, so a test (and a developer) has to be able to read the
 *  code back out. `lastJoinCode` below is what the surface uses. */
interface LocalJoinChallenge {
  id: string;
  crewId: string;
  email: string;
  code: string;
  attempts: number;
  createdAt: number;
  expiresAt: number;
  consumed: boolean;
}

/** The local stand-in for a `crew_join_requests` row. */
interface LocalJoinRequest {
  id: string;
  crewId: string;
  userKey: string;
  requestedAt: number;
  closedAs: '' | 'approved' | 'denied' | 'withdrawn';
}

/** The local stand-in for a `crews` row. */
interface LocalCrew {
  id: string;
  slug: string;
  name: string;
}

/** The local stand-in for a `crew_members` row; `userKey`, not a uuid, because
 *  that is the identity every other local structure is keyed by. */
interface LocalMembership {
  crewId: string;
  userKey: string;
  role: 'owner' | 'player';
  hue: number;
  col: string;
}

/** First hue of the shared palette — `_shared/crew.ts#HUES[0]`, which is what
 *  `hueForRosterCount(0)` returns for a crew with nobody in it yet. */
const OWNER_HUE = 10;

/**
 * The address an account SIGNS IN WITH — `auth.users.email` on the server.
 *
 * `loginEmail` is the truth when there is one; otherwise the account still has
 * the synthesized placeholder, which is what every pre-invitation account has
 * and what the seeding script exists to replace (migration 3029, docs/EMAIL.md).
 *
 * DELIBERATELY NOT `u.email`. That is `profiles.recovery_email`, the legacy
 * note being retired — reading it here is precisely the mistake that made
 * MANAGE CREW disagree with ACCOUNT about Marcus's own address.
 */
function authEmailOf(u: { k: string; loginEmail: string }): string {
  return u.loginEmail || u.k + '@crew.quarterdown.com';
}

/** The crew every seeded member starts in — the local `alpha`. */
const SEED_CREW: LocalCrew = {
  id: '00000000-0000-0000-0000-00000000a1fa',
  slug: 'alpha',
  name: 'The Crew',
};

interface BcastMsg {
  qd: 1;
  table: string;
  actorKey: string;
  scope: 'crew' | 'personal';
}

const memoryStorage = (): StorageLike => {
  const m = new Map<string, string>();
  return {
    getItem: (k) => m.get(k) ?? null,
    setItem: (k, v) => void m.set(k, v),
    removeItem: (k) => void m.delete(k),
  };
};

function defaultStorage(): StorageLike {
  try {
    if (typeof localStorage !== 'undefined') return localStorage;
  } catch {
    /* Safari private mode throws on access */
  }
  return memoryStorage();
}

function defaultChannel(): ChannelLike | null {
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      return new BroadcastChannel(LOCAL_CHANNEL) as unknown as ChannelLike;
    }
  } catch {
    /* not available */
  }
  return null;
}

/**
 * Dev parity for the `steam_tags` / `player_cat_ids` keys the `discover_page`
 * and `games_bootstrap` row JSON carry (both land in `Game.stags` / `Game.pcats`
 * via `SupabaseRepository.toGame`). This is the seam the store tests exercise —
 * there is no Deno runner for the RPCs, so this double is where CI can see the
 * fields flow end to end.
 *
 * The category ids are live `game_features.player_cat_ids` values read back on
 * 2026-08-10, not inventions; the tag lists are representative weight-ordered
 * Steam names. Only a few appids are decorated ON PURPOSE: absence is a state
 * production really serves (an RPC deploy predating the keys, a row the nightly
 * features job has not reached), and a double that decorated every row would
 * make that state invisible in dev.
 */
const STEAM_DISPLAY_FIXTURES: Record<number, { stags: string[]; pcats: number[] }> = {
  // Portal 2 — co-op family present (9/38/39) plus split screen (24).
  620: {
    stags: ['Puzzle', 'Co-op', 'First-Person', 'Sci-fi', 'Comedy', 'Singleplayer', 'Story Rich', 'Funny'],
    pcats: [2, 1, 9, 38, 39, 24],
  },
  // Project Zomboid — co-op and PvP at once; CO-OP must win the coarse label.
  108600: {
    stags: ['Survival', 'Zombies', 'Open World', 'Multiplayer', 'Simulation', 'Sandbox', 'RPG', 'Crafting'],
    pcats: [2, 1, 49, 36, 9, 38, 39, 24, 27],
  },
  // Among Us — multiplayer with NO single-player id at all.
  945360: {
    stags: ['Social Deduction', 'Multiplayer', 'Party Game', 'Space', 'Online Co-Op', 'Funny', '2D', 'Casual'],
    pcats: [1, 49, 36, 47, 9, 38, 48, 27],
  },
  // Baldur's Gate 3 — the reference co-op row the task list was verified with.
  1086940: {
    stags: ['RPG', 'Choices Matter', 'Story Rich', 'Turn-Based Combat', 'CRPG', 'Co-op', 'Fantasy', 'Romance'],
    pcats: [2, 1, 9, 38, 48, 27],
  },
  // Hollow Knight: Silksong — [2] alone: the verified solo-only shape.
  1030300: {
    stags: ['Metroidvania', 'Platformer', 'Souls-like', '2D', 'Atmospheric', 'Difficult', 'Exploration', 'Singleplayer'],
    pcats: [2],
  },
  // Hades II — the second verified solo-only row.
  1145350: {
    stags: ['Action Roguelike', 'Roguelite', 'Mythology', 'Hack and Slash', 'Isometric', 'Story Rich', 'Singleplayer', 'Difficult'],
    pcats: [2],
  },
};

/**
 * Dev parity for `steam_facts.review_score` (migration 3040) — Steam's own
 * review rung, which the nightly pool writes and this double has no pool to run.
 *
 * DERIVED FROM THE PERCENTAGE UNDER ONE STATED ASSUMPTION: every game in the
 * seed is a major release with well over 500 reviews, so the count half of
 * Steam's ladder is satisfied at every rung and the percentage alone picks the
 * right one. That assumption is true of this fixture and false in general,
 * which is exactly why the app stores Steam's rung rather than deriving it.
 *
 * The consequence worth knowing while testing: rung 7 (`Positive` — 80%+ on
 * 10–49 reviews, the case that makes a percentage-derived band lie) has NO
 * seeded example and cannot be reached in dev. `lib/format.test.ts` covers it.
 */
function devRung(rp: number): number | undefined {
  if (rp <= 0) return undefined;
  if (rp >= 95) return 9;
  if (rp >= 80) return 8;
  if (rp >= 70) return 6;
  if (rp >= 40) return 5;
  if (rp >= 20) return 4;
  return 1;
}

/** Decorate in place, the way the RPC row JSON would have arrived decorated. */
function applySteamDisplay(g: Game): void {
  const rs = devRung(g.rp);
  if (rs !== undefined) g.rs = rs;
  const d = STEAM_DISPLAY_FIXTURES[g.appid];
  if (!d) return;
  g.stags = [...d.stags];
  g.pcats = [...d.pcats];
}

/**
 * Dev parity for 2901's per-row `ccu_at` stamp — the freshness gate on
 * `detailVM`'s PLAYING NOW line. Ages are RELATIVE to now() rather than pinned
 * ISO strings: a hard-coded stamp silently crosses the 24-hour line as the
 * clock moves, and the fixture would drift between the very two states it
 * exists to exhibit. One FRESH row and one STALE row on purpose — production
 * serves both (the ~950-row pool rotates through the sampler at 250 a night,
 * so ~¾ of samples are >24h old at any moment), and a double that minted only
 * fresh stamps would make the stale rendering unreachable in dev. Every other
 * row leaves the field ABSENT, the third real state (never sampled / an RPC
 * deploy predating the key).
 *
 * `ccu` itself is deliberately NOT decorated: local-mode ordering reads raw
 * `g.ccu` (`popOf`) and a fixture sample would re-rank every local surface.
 */
const CCU_AT_AGE_HOURS: Record<number, number> = {
  1086940: 2, // sampled overnight — the PLAYING NOW-eligible shape
  620: 72, // three nights unrotated — the stale-sample shape
};

function applyCcuAt(g: Game, now: number): void {
  const age = CCU_AT_AGE_HOURS[g.appid];
  if (age === undefined) return;
  g.ccuAt = new Date(now - age * 36e5).toISOString();
}

// ---------------------------------------------------------------------------

export class LocalRepository implements Repository {
  /** Monotonic, so two invitations minted under a frozen test clock still get
   *  distinct tokens. See `inviteMember`. */
  private inviteSeq = 0;
  /** Sequence for join-link tokens, challenge ids and request ids. Not derived
   *  from the clock, for the reason `inviteSeq` records: the tests freeze
   *  `now`, so two mints in one test would collide. */
  private joinSeq = 0;
  /**
   * The last code "mailed", so local mode can show it.
   *
   * There is no mailer here and there is nothing to protect in a store that
   * already holds every password in `creds` — but the ASK screen genuinely
   * cannot be walked without the code, and a developer guessing six digits is
   * not a workflow. The server stores only a salted hash and can never do this.
   */
  lastJoinCode = '';
  /** Crew addresses are opaque now (3031); this keeps local ones unique
   *  without randomness, so a frozen-clock test still gets two of them. */
  private crewSeq = 0;
  private readonly storage: StorageLike;
  private readonly channel: ChannelLike | null;
  private readonly latencyMs: number;
  private readonly now: () => number;
  private readonly apiBase: string;
  private readonly fetchImpl: typeof fetch | null;

  private readonly crewHandlers = new Set<CrewHandlers>();
  private readonly personalHandlers = new Set<PersonalHandlers>();
  private readonly authHandlers = new Set<(s: AuthSession | null) => void>();
  private readonly onMessage: (ev: { data: unknown }) => void;

  constructor(opts: LocalRepositoryOptions = {}) {
    this.storage = opts.storage ?? defaultStorage();
    this.channel = (opts.channel ?? defaultChannel)();
    this.latencyMs = opts.latencyMs ?? 40;
    this.now = opts.now ?? (() => Date.now());
    this.apiBase = opts.apiBase ?? '';
    this.fetchImpl =
      opts.fetchImpl ?? (typeof fetch === 'function' ? fetch.bind(globalThis) : null);

    this.onMessage = (ev) => {
      const m = ev.data as BcastMsg | null;
      if (!m || m.qd !== 1) return;
      if (m.scope === 'personal') {
        for (const h of this.personalHandlers) h.onChange({ table: m.table });
      } else {
        for (const h of this.crewHandlers) h.onChange({ table: m.table, actorKey: m.actorKey });
      }
    };
    this.channel?.addEventListener('message', this.onMessage);

    this.read(); // seed on first construction
  }

  /** Detach the channel. The store calls this on teardown; tests call it too. */
  dispose(): void {
    this.channel?.removeEventListener('message', this.onMessage);
    this.channel?.close();
    this.crewHandlers.clear();
    this.personalHandlers.clear();
    this.authHandlers.clear();
  }

  // -- storage --------------------------------------------------------------

  private seedDb(): LocalDb {
    const now = this.now();
    return {
      v: 1,
      users: seedUsers(now),
      creds: seedCredentials(),
      list: seedList(now),
      pts: ptSeed(now),
      videos: [],
      session: '',
      potentials: structuredCopy(SEED_POTENTIALS),
      tiers: structuredCopy(SEED_TIERS),
      tierOrder: structuredCopy(SEED_TIER_ORDER),
      skips: {},
      intOrder: {},
      intSkips: {},
      backlog: seedBacklog(now),
      scores: seedScores(now),
      sends: seedSends(now),
      watch: seedWatch(),
      boardEvents: seedEvents(now),
      mutes: {},
      steam: {},
      events: [],
      facts: {},
      assets: {},
      extraGames: [],
      remembered: {},
      crews: [{ ...SEED_CREW }],
      invites: [],
      joinLinks: [],
      joinChallenges: [],
      joinRequests: [],
      recovery: null,
      round: null,
      sched: {},
      memberships: seedUsers(now).map((u) => ({
        crewId: SEED_CREW.id,
        userKey: u.k,
        // The seed's first admin is the owner, everybody else a player - the
        // same mapping migration 3014's backfill applied to real data.
        role: u.role === 'admin' ? ('owner' as const) : ('player' as const),
        hue: u.hue,
        col: u.col,
      })),
      activeCrewId: SEED_CREW.id,
    };
  }

  private read(): LocalDb {
    let raw: string | null = null;
    try {
      raw = this.storage.getItem(LOCAL_DB_KEY);
    } catch {
      raw = null;
    }
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as Partial<LocalDb>;
        // "If saved.users is non-empty, saved data wins entirely" — prototype boot(), line 1399.
        if (parsed && Array.isArray(parsed.users) && parsed.users.length) {
          const db: LocalDb = { ...this.seedDb(), ...parsed, v: 1 };
          // Winning entirely is exactly why the migrate map belongs here: the
          // seed runs `sanitizeVotes` on the way in, and a save written before
          // the 4-tier widening bypasses it forever. Its `y`/`n` would arrive
          // as calls nothing counts — `agg` ignores them and the owner's own
          // board files those games under NO CALL YET. The pass is idempotent,
          // and the next `write()` persists the migrated save.
          db.list = db.list.map((e) => ({ ...e, votes: sanitizeVotes(e.votes) }));
          // Remembered calls are calls, so they take the same pass — a save
          // written before the 4-tier widening can be carrying y/n in here too,
          // and a stash that skipped the migrate map would hand them back as
          // letters nothing counts the moment a game was re-added. `remembered`
          // is absent from saves written before this feature; the seedDb spread
          // above has already supplied `{}` for those.
          for (const [appid, bag] of Object.entries(db.remembered)) {
            db.remembered[appid] = sanitizeVotes(bag);
          }
          // Multi-crew fields arrived in phase C, so a save written before it
          // inherits the seed's crews through the spread above - but the seed's
          // MEMBERSHIPS were computed from a fresh roster and know nothing
          // about anybody this save added since. Backfill the stragglers into
          // the active crew, which is the same "earliest admin owns it, the
          // rest play" mapping migration 3014 applied server-side.
          //
          // Guarded on there BEING an active crew, because this runs on every
          // load and not just on an old save: deleting your last crew empties
          // `memberships` and blanks `activeCrewId`, and an unguarded backfill
          // then re-created the whole roster inside a crew with no id — a
          // nameless row in the switcher for a crew that does not exist.
          const known = new Set(db.memberships.map((m) => m.userKey));
          /* NEVER BACKFILL SOMEBODY WHO ASKED THROUGH A JOIN LINK (3056).
             This loop treats "has an account but no membership" as an old save
             to repair, which was the only way to be in that state — until join
             links made it a DELIBERATE one. An asker is member-less on purpose,
             and backfilling them here promotes them into the party without the
             leader ever deciding, which is the exact thing the whole feature
             exists to prevent.
             Every row in `joinRequests` is excluded, not just the open ones: a
             denied or withdrawn asker still legitimately has an account and no
             membership, and re-adding them would be worse than leaving them
             out. */
          const asked = new Set((db.joinRequests ?? []).map((r) => r.userKey));
          for (const u of db.users) {
            if (known.has(u.k) || asked.has(u.k) || !db.activeCrewId) continue;
            db.memberships.push({
              crewId: db.activeCrewId,
              userKey: u.k,
              role: 'player',
              hue: u.hue,
              col: u.col,
            });
          }
          return db;
        }
      } catch {
        /* corrupt payload — fall through and re-seed */
      }
    }
    const fresh = this.seedDb();
    this.write(fresh);
    return fresh;
  }

  private write(db: LocalDb): void {
    try {
      this.storage.setItem(LOCAL_DB_KEY, JSON.stringify(db));
    } catch {
      /* quota / private mode — the in-memory copy still works for this tab */
    }
  }

  /** Read-modify-write + broadcast. Every mutation goes through here. */
  private mutateDb(table: string, fn: (db: LocalDb) => void, scope: 'crew' | 'personal' = 'crew'): LocalDb {
    const db = this.read();
    fn(db);
    this.write(db);
    this.broadcast(table, db.session, scope);
    return db;
  }

  private broadcast(table: string, actorKey: string, scope: 'crew' | 'personal'): void {
    const msg: BcastMsg = { qd: 1, table, actorKey, scope };
    try {
      this.channel?.postMessage(msg);
    } catch {
      /* channel closed */
    }
  }

  private lat(): Promise<void> {
    if (!this.latencyMs) return Promise.resolve();
    const ms = Math.floor(Math.random() * this.latencyMs);
    return new Promise((res) => setTimeout(res, ms));
  }

  private meKey(db: LocalDb): string {
    if (!db.session) throw new RepoError('NOT_SIGNED_IN');
    return db.session;
  }

  // -- identity -------------------------------------------------------------

  async loginRoster(_crewSlug: string): Promise<RosterEntry[]> {
    await this.lat();
    return this.read().users.map((u) => ({
      userKey: u.k,
      displayName: u.name,
      hue: u.hue,
      col: u.col,
      avatarUrl: u.av,
    }));
  }

  /**
   * ONE FIELD, TWO IDENTITIES — the same rule `loginEmail` applies against the
   * real Auth: an `@` means it is already an address, anything else is a player
   * name.
   *
   * This matched the KEY only, which was invisible while every account signed
   * in by name and wrong the moment one did not. Two things depend on it now:
   * the login box says EMAIL, so `dev:local` would refuse everybody who typed
   * one; and an invited account's login IS the address the invitation went to,
   * so the sign-in immediately after a first-ever join could never succeed —
   * which is exactly the path that ended in a blank screen on staging.
   */
  async signIn(identity: string, password: string): Promise<AuthSession> {
    await this.lat();
    const db = this.read();
    const want = identity.trim().toLowerCase();
    const u = want.includes('@')
      ? db.users.find((x) => authEmailOf(x).toLowerCase() === want)
      : db.users.find((x) => x.k === want);
    if (!u) throw new RepoError('NO_SUCH_USER', 'PICK A PLAYER FIRST');
    // Keyed by the RESOLVED account, not by what was typed — otherwise signing
    // in by address looks up a credential filed under a key that is not one.
    if ((db.creds[u.k] ?? '') !== password) throw new RepoError('WRONG_PASSWORD', 'WRONG PASSWORD');
    db.session = u.k;
    this.write(db);
    const session: AuthSession = {
      userId: u.k,
      userKey: u.k,
      authEmail: authEmailOf(u),
      mustReset: u.mustReset,
    };
    for (const cb of this.authHandlers) cb(session);
    return session;
  }

  async setPassword(next: string): Promise<void> {
    await this.lat();
    this.mutateDb('profiles', (db) => {
      const k = this.meKey(db);
      db.creds[k] = next;
      db.users = db.users.map((u) => (u.k === k ? { ...u, mustReset: false } : u));
      // A RECOVERY LINK IS SPENT BY THE PASSWORD IT SETS. GoTrue invalidates
      // the token the moment it is used; without this, local mode would let the
      // same emailed link be replayed for ever — a forwarded link would still
      // open a set-a-password screen after the account had already recovered.
      if (db.recovery?.userKey === k) db.recovery = null;
    });
  }

  /**
   * Changes the LOGIN address (`auth.users.email`), which is what Profile's
   * CHANGE EMAIL does on the server — not the legacy profile note. Writing
   * `email` here was correct only while the two were the same field.
   */
  async changeEmail(next: string): Promise<void> {
    await this.lat();
    const db = this.read();
    const email = next.trim().toLowerCase();
    // Auth enforces one address per account; local mode has to refuse the same
    // way or it would succeed where production does not.
    if (db.users.some((u) => u.k !== db.session && authEmailOf(u).toLowerCase() === email)) {
      throw new RepoError('EMAIL_TAKEN', 'That email is already in use');
    }
    this.mutateDb('profiles', (d) => {
      d.users = d.users.map((u) => (u.k === d.session ? { ...u, loginEmail: email } : u));
    });
  }

  async reauth(current: string): Promise<boolean> {
    await this.lat();
    const db = this.read();
    return (db.creds[this.meKey(db)] ?? '') === current;
  }

  /**
   * FORGOT PASSWORD, offline. There is no mail here, so the "sent" is the whole
   * behaviour — and modelling it as a no-op is not laziness but the point: the
   * real one is deliberately indistinguishable for a known and an unknown
   * address, so a local mode that refused unknown ones would let a surface be
   * built against a verdict production never gives.
   *
   * The recovery link this WOULD have mailed is minted anyway, so local mode
   * can walk the second half of the flow (`resumeRecovery`) without a mailbox.
   */
  async sendPasswordReset(email: string): Promise<void> {
    await this.lat();
    const addr = email.trim().toLowerCase();
    this.mutateDb('profiles', (db) => {
      const u = db.users.find((x) => authEmailOf(x).toLowerCase() === addr);
      // Unknown address: nothing is minted and nothing is said, exactly as
      // above. The caller cannot tell this branch from the one below.
      db.recovery = u ? { token: 'local-recovery-' + u.k, userKey: u.k } : null;
    });
  }

  async resumeRecovery(link: RecoveryLink): Promise<AuthSession> {
    await this.lat();
    const db = this.read();
    const rec = db.recovery;
    if (!rec || rec.token !== link.accessToken) {
      throw new RepoError('LINK_EXPIRED', 'That reset link has expired');
    }
    const u = db.users.find((x) => x.k === rec.userKey);
    if (!u) throw new RepoError('LINK_EXPIRED', 'That reset link has expired');
    db.session = u.k;
    this.write(db);
    const session: AuthSession = {
      userId: u.k,
      userKey: u.k,
      authEmail: authEmailOf(u),
      mustReset: u.mustReset,
    };
    for (const cb of this.authHandlers) cb(session);
    return session;
  }

  /** The link `sendPasswordReset` would have emailed, for tests and local mode.
   *  Not on `Repository`: nothing in the app may read a token it was not sent. */
  peekRecoveryLink(): RecoveryLink | null {
    const rec = this.read().recovery;
    return rec ? { accessToken: rec.token, refreshToken: rec.token + '-r' } : null;
  }

  async signOut(): Promise<void> {
    await this.lat();
    const db = this.read();
    db.session = '';
    this.write(db);
    for (const cb of this.authHandlers) cb(null);
  }

  async currentSession(): Promise<AuthSession | null> {
    await this.lat();
    const db = this.read();
    if (!db.session) return null;
    const u = db.users.find((x) => x.k === db.session);
    if (!u) return null;
    return {
      userId: u.k,
      userKey: u.k,
      authEmail: authEmailOf(u),
      mustReset: u.mustReset,
    };
  }

  onAuthChange(cb: (s: AuthSession | null) => void): Unsub {
    this.authHandlers.add(cb);
    return () => this.authHandlers.delete(cb);
  }

  // -- boot -----------------------------------------------------------------

  async fetchProfile(userId: string): Promise<Profile> {
    await this.lat();
    const u = this.read().users.find((x) => x.k === userId);
    if (!u) throw new RepoError('NOT_FOUND');
    return {
      userId: u.k,
      userKey: u.k,
      displayName: u.name,
      role: u.role,
      hue: u.hue,
      recoveryEmail: u.email,
      mustReset: u.mustReset,
      avatarUrl: u.av,
      lastReviewedAt: u.lastRev,
    };
  }

  async fetchCrewSnapshot(_crewId: string): Promise<CrewSnapshot> {
    await this.lat();
    const db = this.read();
    const me = db.users.find((u) => u.k === db.session) ?? null;
    const muted = new Set(db.mutes[db.session] ?? []);
    const crew = db.crews.find((c) => c.id === db.activeCrewId) ?? null;
    // Membership decides the roster and carries the per-crew hue/col/role,
    // mirroring what the server read does through `crew_members` (D9). You are
    // always on your own roster, which is also what keeps the no-crew state
    // from rendering an empty Profile.
    const mine = new Map(
      db.memberships.filter((m) => m.crewId === db.activeCrewId).map((m) => [m.userKey, m]),
    );
    /* A CREW YOU ARE NOT IN IS NOT YOUR ACTIVE CREW (3056). `db.activeCrewId`
       is a single global here, where the server reads `profiles.active_crew_id`
       PER ACCOUNT — and for somebody who has only asked to join, that column is
       NULL. Without this the local twin hands a member-less account the seed
       crew, `activeCrew` is never null, and the crewless boot branch (and with
       it the WAITING screen) is unreachable in dev and untestable. */
    const inCrew = Boolean(db.session) && mine.has(db.session);
    return {
      crew: crew && inCrew
        ? { id: crew.id, name: crew.name, slug: crew.slug, createdAt: this.now() }
        : null,
      members: db.users
        .filter((u) => mine.has(u.k) || u.k === db.session)
        .map((u) => {
          const m = mine.get(u.k);
          return {
            ...u,
            role: m ? (m.role === 'owner' ? ('admin' as const) : ('user' as const)) : u.role,
            hue: m ? m.hue : u.hue,
            col: m ? m.col : u.col,
            seen: [...u.seen],
          };
        }),
      entries: db.list.map((e) => ({ ...e, votes: { ...e.votes }, comments: [...e.comments] })),
      playtests: db.pts.filter((p) => !muted.has(p.id)).map(clonePt),
      seen: me ? [...me.seen] : [],
      lastReviewedAt: me ? me.lastRev : 0,
      events: [...db.events],
      videos: (db.videos ?? []).map((v) => ({ ...v })),
    };
  }

  // == crews (multi-crew phase C) ============================================

  async myCrews(): Promise<CrewMembership[]> {
    await this.lat();
    const db = this.read();
    return db.memberships
      .filter((m) => m.userKey === db.session)
      .map((m) => {
        const crew = db.crews.find((c) => c.id === m.crewId);
        return {
          crewId: m.crewId,
          slug: crew?.slug ?? '',
          name: crew?.name ?? '',
          role: m.role,
          memberCount: db.memberships.filter((x) => x.crewId === m.crewId).length,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name) || a.crewId.localeCompare(b.crewId));
  }

  async setActiveCrew(crewId: string): Promise<void> {
    await this.lat();
    const db = this.read();
    // The server RPC raises 42501 for a crew you are not in; refusing here too
    // keeps local mode from silently succeeding where production would not.
    if (!db.memberships.some((m) => m.crewId === crewId && m.userKey === db.session)) {
      throw new RepoError('FORBIDDEN', 'Not a member of that crew');
    }
    // 'profiles' because active_crew_id lives there server-side, and the
    // switch is personal: no other member's view changes.
    this.mutateDb(
      'profiles',
      (d) => {
        d.activeCrewId = crewId;
      },
      'personal',
    );
  }

  async createCrew(input: CreateCrewInput): Promise<CrewMembership> {
    await this.lat();
    const db = this.read();
    const trimmed = input.name.trim();
    if (!trimmed) throw new RepoError('BAD_REQUEST', 'Name your crew');

    /* AN OPAQUE ADDRESS, NOT ONE DERIVED FROM THE NAME (3031). This used to
       mirror `crewSlugBase` and bump on collision, which is what the database
       did until today; keeping that here would let local mode model a rule
       production no longer has — and would keep a name like `!!!` failing
       locally while the server accepts it.

       Same alphabet as `new_crew_slug`, minus the look-alikes, and the same
       collision loop. Tombstones have no local equivalent, so a deleted crew's
       slug IS reusable here: noted rather than faked, because that table is the
       server's memory and there is nothing local to remember it with. */
    // A COUNTER, NOT `Math.random()`, for the reason the invite token uses one:
    // a test that freezes time still needs two crews to get two addresses, and
    // a random one would make assertions flaky for no gain. Local mode needs
    // OPAQUE and UNIQUE; unguessable is meaningless with the store on disk.
    const seq = ++this.crewSeq;
    let slug = 'crew-' + seq.toString(36).padStart(6, 'a');
    for (let n = 2; db.crews.some((c) => c.slug === slug); n++) {
      slug = 'crew-' + (seq * 100 + n).toString(36).padStart(6, 'a');
    }

    const id = slug + '-' + String(db.crews.length + 1);

    // Signed-out arm: there is no session yet, so the account has to exist
    // before it can own anything. The server does this inside create_crew_tx;
    // locally it is a user row, a credential and a session, in that order.
    const acc = input.account;
    if (acc) {
      const key = acc.displayName
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .slice(0, 24);
      if (key.length < 2) throw new RepoError('BAD_REQUEST', 'Use a longer display name');
      if (db.users.some((u) => u.k === key)) {
        throw new RepoError('KEY_TAKEN', 'That name is taken');
      }
      this.mutateDb('profiles', (d) => {
        d.users.push({
          k: key,
          name: acc.displayName.trim(),
          role: 'admin',
          hue: OWNER_HUE,
          col: '',
          email: acc.email.trim(),
          // The wizard collects a REAL address and the account signs in with it,
          // so an owner created this way never needs seeding.
          loginEmail: acc.email.trim().toLowerCase(),
          mustReset: false,
          av: '',
          lastRev: this.now(),
          seen: [],
        });
        d.creds[key] = acc.password;
        d.session = key;
      });
    }
    // A brand-new crew has a roster of 0, so its owner takes the first hue -
    // the same answer `hueForRosterCount(0)` gives in `_shared/crew.ts`.
    const hue = OWNER_HUE;
    this.mutateDb('crew_members', (d) => {
      d.crews.push({ id, slug, name: trimmed });
      d.memberships.push({ crewId: id, userKey: d.session, role: 'owner', hue, col: '' });
      d.activeCrewId = id;
    });
    return { crewId: id, slug, name: trimmed, role: 'owner', memberCount: 1 };
  }

  async deleteCrew(crewId: string, confirmName: string): Promise<void> {
    await this.lat();
    const db = this.read();
    const crew = db.crews.find((c) => c.id === crewId);
    if (!crew) throw new RepoError('NOT_FOUND', 'No such party');
    if (!db.memberships.some((m) => m.crewId === crewId && m.userKey === db.session && m.role === 'owner')) {
      throw new RepoError('FORBIDDEN', 'Only the crew owner can delete it');
    }
    if (confirmName.trim() !== crew.name) {
      throw new RepoError('BAD_REQUEST', 'The crew name does not match');
    }
    this.mutateDb('crew_members', (d) => {
      d.crews = d.crews.filter((c) => c.id !== crewId);
      d.memberships = d.memberships.filter((m) => m.crewId !== crewId);
      if (d.activeCrewId === crewId) {
        // Same fallback as my_crew_id() step (b): any remaining membership,
        // else no crew at all.
        d.activeCrewId = d.memberships.find((m) => m.userKey === d.session)?.crewId ?? '';
      }
    });
  }

  /**
   * The GOTY year the last `fetchMyBoards` read (AA phase 5). The local db keys
   * the CURRENT year's boards by the bare user key — every save persisted before
   * "one list per year" is that year's — and any other year by `key@year`.
   */
  private boardYear = GOTY_YEAR;
  private yk(k: string, year: number = this.boardYear): string {
    return year === GOTY_YEAR ? k : k + '@' + year;
  }

  async fetchMyBoards(userId: string, year: number = this.boardYear): Promise<MyBoards> {
    await this.lat();
    this.boardYear = year;
    const db = this.read();
    const yk = this.yk(userId, year);
    return {
      potentials: [...(db.potentials[yk] ?? [])],
      tiers: { ...(db.tiers[yk] ?? {}) },
      tierOrder: [...(db.tierOrder[yk] ?? [])],
      skips: [...(db.skips[yk] ?? [])],
      intOrder: [...(db.intOrder[userId] ?? [])],
      intSkips: [...(db.intSkips?.[userId] ?? [])],
    };
  }

  async fetchMemberGoty(userKey: string): Promise<MemberGotyYear[]> {
    await this.lat();
    const db = this.read();
    const out: MemberGotyYear[] = [];
    for (const k of Object.keys(db.potentials)) {
      let year: number | null = null;
      if (k === userKey) year = GOTY_YEAR;
      else {
        const m = /^(.+)@(\d{4})$/.exec(k);
        if (m && m[1] === userKey) year = Number(m[2]);
      }
      if (year === null || !(db.potentials[k] ?? []).length) continue;
      out.push({
        year,
        potentials: [...(db.potentials[k] ?? [])],
        tiers: { ...(db.tiers[k] ?? {}) },
        tierOrder: [...(db.tierOrder[k] ?? [])],
      });
    }
    return out.sort((a, b) => b.year - a.year);
  }

  async fetchGotyYears(userId: string): Promise<number[]> {
    await this.lat();
    const db = this.read();
    const years = new Set<number>();
    for (const k of Object.keys(db.potentials)) {
      if (k === userId && (db.potentials[k] ?? []).length) years.add(GOTY_YEAR);
      const m = /^(.+)@(\d{4})$/.exec(k);
      if (m && m[1] === userId && (db.potentials[k] ?? []).length) years.add(Number(m[2]));
    }
    return [...years].sort((a, b) => b - a);
  }

  /**
   * 3059's local double. The db's `intOrder` is keyed by user key here (local
   * mode's `session` IS the key), so a crewmate's ordering is simply theirs —
   * no membership gate to model, because the local db is one crew.
   *
   * `[]` for a member who has ranked nothing, exactly as the RPC answers, and
   * for an unknown key too: the function deliberately cannot tell those apart
   * and neither should the double.
   */
  async fetchCrewInterestOrder(userKey: string): Promise<GameId[]> {
    await this.lat();
    return [...(this.read().intOrder[userKey] ?? [])];
  }

  // -- my games (AA phase 2; 3060's local doubles) --------------------------

  async fetchBacklog(): Promise<BacklogEntry[]> {
    await this.lat();
    const db = this.read();
    // `?? {}` on the map: a save persisted before 3060 has no `backlog` key —
    // the local store is a real upgrade path, not a fresh fixture.
    return [...((db.backlog ?? {})[this.meKey(db)] ?? [])];
  }

  async addBacklog(appid: GameId): Promise<void> {
    await this.lat();
    // Personal, not crew: a shelf is yours alone and no other member's view
    // changes, so only this account's other tabs are told.
    this.mutateDb('backlog', (db) => {
      const k = this.meKey(db);
      db.backlog = db.backlog ?? {};
      const cur = db.backlog[k] ?? [];
      if (cur.some((e) => e.id === appid)) return;
      db.backlog[k] = [...cur, { id: appid, at: this.now() }];
    }, 'personal');
  }

  async addBacklogMany(appids: GameId[]): Promise<void> {
    await this.lat();
    // One mutateDb, so one broadcast — the bulk write's whole point.
    this.mutateDb('backlog', (db) => {
      const k = this.meKey(db);
      db.backlog = db.backlog ?? {};
      const cur = db.backlog[k] ?? [];
      const have = new Set(cur.map((e) => e.id));
      const at = this.now();
      const add: BacklogEntry[] = [];
      for (const id of appids) {
        if (have.has(id)) continue;
        have.add(id);
        add.push({ id, at });
      }
      if (add.length) db.backlog[k] = [...cur, ...add];
    }, 'personal');
  }

  /**
   * No wishlist to read locally, so this answers with the seeded catalog's
   * UPCOMING games — the rows the prototype itself labels WISHLIST ON STEAM.
   * Same gates as `steamFill`: unlinked throws, a private profile is
   * `{open: false}`, matching the wire contract.
   */
  async steamWishlist(): Promise<SteamWishlistResult> {
    await this.lat();
    const db = this.read();
    const link = db.steam[this.meKey(db)];
    if (!link || !link.linked) throw new RepoError('NOT_LINKED');
    if (!link.profilePublic) {
      return { open: false, games: [], total: 0, notGames: 0, unresolved: 0, unnamed: 0 };
    }
    const games = seedCatalog()
      .filter((g) => g.up)
      .map((g) => ({ appid: g.appid, name: g.n, dev: g.dev, upcoming: true }));
    return { open: true, games, total: games.length, notGames: 0, unresolved: 0, unnamed: 0 };
  }

  async removeBacklog(appid: GameId): Promise<void> {
    await this.lat();
    this.mutateDb('backlog', (db) => {
      const k = this.meKey(db);
      db.backlog = db.backlog ?? {};
      db.backlog[k] = (db.backlog[k] ?? []).filter((e) => e.id !== appid);
    }, 'personal');
  }

  /**
   * 3060's crew read, local double. The local db is one crew, so every user's
   * scores are the party's scores — no membership gate to model.
   */
  async fetchScores(): Promise<CrewScoreRow[]> {
    await this.lat();
    const out: CrewScoreRow[] = [];
    for (const [userKey, byGame] of Object.entries(this.read().scores ?? {})) {
      for (const [id, rec] of Object.entries(byGame)) {
        out.push({ userKey, id: Number(id), v: rec.v, at: rec.at, prev: rec.prev ?? null });
      }
    }
    return out;
  }

  /**
   * 3062's read, local double: the rows I am party to. The policy's crew
   * bound has nothing to model here — one crew — but the two-party bound
   * does, so a third member's tab never learns about a send between two
   * others.
   */
  async fetchWatch(): Promise<Record<string, WatchRec>> {
    await this.lat();
    const db = this.read();
    const mine = (db.watch ?? {})[this.meKey(db)] ?? {};
    const out: Record<string, WatchRec> = {};
    for (const [gid, rec] of Object.entries(mine)) out[gid] = { ...rec };
    return out;
  }

  async putWatch(appid: GameId, rec: WatchRec | null): Promise<void> {
    await this.lat();
    const armed = rec && (rec.ea || rec.date || rec.price || rec.sale || rec.demo || rec.upd || rec.dlc);
    // Personal, like the shelf: nobody else's view changes.
    this.mutateDb('game_watch', (db) => {
      const k = this.meKey(db);
      db.watch = db.watch ?? {};
      const mine = { ...(db.watch[k] ?? {}) };
      if (armed && rec) mine[String(appid)] = { ...rec, cap: Math.max(1, Math.round(rec.cap)) };
      else delete mine[String(appid)];
      db.watch[k] = mine;
    }, 'personal');
  }

  /**
   * 3063's read, local double: PARTY rows from every other account (one crew
   * locally, so every score is the party's), and the WORLD rows written for
   * me. The 14-day window is the reader's.
   */
  async fetchBoard(): Promise<BoardEvent[]> {
    await this.lat();
    const db = this.read();
    const me = this.meKey(db);
    const cutoff = this.now() - 14 * 864e5;
    return (db.boardEvents ?? [])
      .filter((e) => e.at >= cutoff && (e.recipient === me || (e.recipient === null && e.actor !== null)))
      .map(({ recipient: _r, ...e }) => ({ ...e, payload: { ...e.payload } }));
  }

  async fetchSends(): Promise<SendRec[]> {
    await this.lat();
    const db = this.read();
    const me = this.meKey(db);
    return (db.sends ?? []).filter((r) => r.from === me || r.to === me).map((r) => ({ ...r }));
  }

  async setSends(appid: GameId, to: string[], note: string, edit: boolean): Promise<void> {
    await this.lat();
    const n = note.trim();
    if (to.length > 0 && (n === '' || n.length > 160)) throw new RepoError('INTERNAL', 'BAD_NOTE');
    if (to.length === 0 && !edit) throw new RepoError('INTERNAL', 'NO_RECIPIENTS');
    // CREW scope: the recipients' TO YOU changes, so their tabs are told.
    this.mutateDb('sends', (db) => {
      const me = this.meKey(db);
      const now = this.now();
      const rows = db.sends ?? [];
      const mine = (r: SendRec) => r.from === me && r.gid === appid;
      if (me === '' || to.includes(me)) throw new RepoError('INTERNAL', 'SELF_SEND');
      for (const k of to) if (!db.users.some((u) => u.k === k)) throw new RepoError('INTERNAL', 'NOT_A_MEMBER');
      const fresh = (k: string, i: number): SendRec => ({
        id: 's' + (now + i).toString(36),
        from: me,
        to: k,
        gid: appid,
        note: n,
        at: now,
        st: '',
      });
      if (edit) {
        const kept = rows.filter((r) => !mine(r) || to.includes(r.to)).map((r) => (mine(r) ? { ...r, note: n } : r));
        const have = new Set(kept.filter(mine).map((r) => r.to));
        db.sends = [...kept, ...to.filter((k) => !have.has(k)).map(fresh)];
      } else {
        db.sends = [...rows.filter((r) => !(mine(r) && to.includes(r.to))), ...to.map(fresh)];
      }
    }, 'crew');
  }

  async answerSend(id: string, st: SendStatus): Promise<void> {
    await this.lat();
    this.mutateDb('sends', (db) => {
      const me = this.meKey(db);
      const row = (db.sends ?? []).find((r) => r.id === id);
      if (!row || row.to !== me) throw new RepoError('INTERNAL', 'NOT_YOURS');
      row.st = st;
    }, 'crew');
  }

  async setScore(appid: GameId, score: number | null): Promise<void> {
    await this.lat();
    // CREW scope: the party sees a score, so every member's tab is told — the
    // same broadcast a vote gets, for the same reason.
    this.mutateDb('game_scores', (db) => {
      const k = this.meKey(db);
      db.scores = db.scores ?? {};
      const mine = { ...(db.scores[k] ?? {}) };
      const was = mine[String(appid)];
      const prev = was?.v;
      if (score === null) delete mine[String(appid)];
      // The local twin of 3067's `tg_game_scores_prev`: a changed score keeps
      // the one it replaced; the same number again keeps whatever was there.
      else if (was === undefined) mine[String(appid)] = { v: score, at: this.now() };
      else if (was.v === score) mine[String(appid)] = { ...was, at: this.now() };
      else mine[String(appid)] = { v: score, at: this.now(), prev: was.v };
      db.scores[k] = mine;
      // The board (3063): what the trigger on game_scores writes — a row per
      // score, the previous value on a re-score, and a retraction on clear.
      db.boardEvents = db.boardEvents ?? [];
      if (score === null) {
        db.boardEvents = db.boardEvents.filter((e) => !(e.kind === 'score' && e.actor === k && String(e.gid) === String(appid)));
      } else {
        db.boardEvents.push({
          id: 'sc:' + k + ':' + appid + ':' + this.now().toString(36),
          at: this.now(),
          kind: 'score',
          gid: Number(appid),
          actor: k,
          payload: prev === undefined ? { v: score } : { v: score, prev },
          recipient: null,
        });
      }
    });
  }

  /**
   * `appids` is accepted and IGNORED, deliberately.
   *
   * The Supabase path bounds this through `games_bootstrap` because production
   * has hundreds of rows and PostgREST truncates at a thousand. The local db is
   * the seed plus whatever this session added — tens of rows — so there is
   * nothing to bound, and inventing a limit here would make the dev double
   * behave unlike both the server and its own previous self for no gain.
   */
  async fetchCatalog(_appids: number[] = []): Promise<Game[]> {
    await this.lat();
    const db = this.read();
    const rows = [...seedCatalog(), ...db.extraGames.map((g) => ({ ...g }))];
    // `coalesce(fact, curated)` — the same precedence as the prototype's
    // `Object.assign(g, fix)` in cat() (line 1432) and as v_games (§1.4).
    for (const g of rows) applyFact(g, db.facts[String(g.appid)]);
    // games_bootstrap decorates its row JSON with the Steam display fields
    // and the ccu_at sample stamp.
    for (const g of rows) {
      applySteamDisplay(g);
      applyCcuAt(g, this.now());
    }
    return rows;
  }

  async fetchSteamLink(userId: string): Promise<SteamLink | null> {
    await this.lat();
    return this.read().steam[userId] ?? null;
  }

  // -- profile --------------------------------------------------------------

  async savePf(p: { displayName: string; recoveryEmail: string }): Promise<void> {
    await this.lat();
    this.mutateDb('profiles', (db) => {
      const k = this.meKey(db);
      db.users = db.users.map((u) =>
        u.k === k ? { ...u, name: p.displayName, email: p.recoveryEmail } : u,
      );
    });
  }

  async onPickAv(dataUrl: string): Promise<{ avatarUrl: string }> {
    await this.lat();
    this.mutateDb('profiles', (db) => {
      const k = this.meKey(db);
      db.users = db.users.map((u) => (u.k === k ? { ...u, av: dataUrl } : u));
    });
    return { avatarUrl: dataUrl };
  }

  /** Same two-argument contract as the RPC: tile pick `(hue, null)` clears the
   * custom color, custom apply `(null, hex)` leaves hue standing. */
  async setColor(hue: number | null, col: string | null): Promise<void> {
    await this.lat();
    this.mutateDb('profiles', (db) => {
      const k = this.meKey(db);
      db.users = db.users.map((u) =>
        u.k === k ? { ...u, hue: hue ?? u.hue, col: col ?? '' } : u,
      );
    });
  }

  async dropAvatar(): Promise<void> {
    await this.lat();
    this.mutateDb('profiles', (db) => {
      const k = this.meKey(db);
      db.users = db.users.map((u) => (u.k === k ? { ...u, av: '' } : u));
    });
  }

  // -- list / votes / comments ---------------------------------------------

  async ensureGame(p: ProvisionalGame): Promise<number> {
    await this.lat();
    this.mutateDb('catalog_games', (db) => {
      const known =
        seedCatalog().some((g) => g.appid === p.appid) || db.extraGames.some((g) => g.appid === p.appid);
      if (known) return; // NEVER overwrite a curated row (ARCHITECTURE §1.12)
      db.extraGames.push({
        id: p.appid,
        appid: p.appid,
        n: p.name,
        dev: p.dev,
        rd: p.releaseYm,
        p: p.price,
        o: p.oldPrice,
        mn: 0,
        mx: 0,
        plat: 'P',
        tags: [],
        rp: 0,
        pk: 0,
        ccu: 0,
        up: p.upcoming,
        ds: p.description,
        ea: false,
        demo: false,
        dAid: 0,
        art: p.artUrl,
        live: false,
        thumb: '',
        // `is_curated = false` in the real schema: this row was minted, not
        // curated, which is what keeps it out of the browse surfaces.
        prov: true,
      });
    });
    return p.appid;
  }

  async quickAdd(appid: GameId): Promise<ListEntry> {
    await this.lat();
    const cur = this.read();
    const k = this.meKey(cur);
    if (cur.list.some((e) => e.id === appid)) throw new RepoError('DUPLICATE', 'ALREADY ON THE LIST');
    const created: ListEntry = {
      id: appid,
      entryId: localEntryId(appid),
      by: k,
      at: this.now(),
      // Adding a game the crew has ranked before brings its calls back with it.
      // Attribution and list position do not come back: `by` and `at` belong to
      // whoever is adding it now.
      votes: { ...(cur.remembered[String(appid)] ?? {}) },
      comments: [],
    };
    this.mutateDb('list_entries', (db) => {
      db.list = [...db.list, created];
      delete db.remembered[String(appid)];
      db.events.push(evt('list_add', k, 'game', String(appid), this.now()));
    });
    return created;
  }

  async removeFromList(entryId: EntryId): Promise<void> {
    await this.lat();
    this.mutateDb('list_entries', (db) => {
      const gone = db.list.find((e) => e.entryId === entryId);
      // Stash before the filter drops the entry and its bag with it. A full
      // replace, not a merge: a member who cleared their call has no entry in
      // this bag and must not have an older one resurrect on re-add. Absence is
      // the "no call" state on both sides of a removal (ARCHITECTURE §1.5).
      if (gone) {
        if (Object.keys(gone.votes).length) db.remembered[String(gone.id)] = { ...gone.votes };
        else delete db.remembered[String(gone.id)];
      }
      db.list = db.list.filter((e) => e.entryId !== entryId);
    });
  }

  async setVote(entryId: EntryId, value: VoteValue | null): Promise<void> {
    await this.lat();
    this.mutateDb('votes', (db) => {
      const k = this.meKey(db);
      db.list = db.list.map((e) => {
        if (e.entryId !== entryId) return e;
        const votes = { ...e.votes };
        // Absence is the "no call" state — clearing DELETEs (ARCHITECTURE §1.5).
        if (value) votes[k] = value;
        else delete votes[k];
        return { ...e, votes };
      });
      db.events.push(evt('vote', k, 'game', entryId, this.now()));
    });
  }

  async setIntAt(order: GameId[]): Promise<void> {
    await this.lat();
    this.mutateDb('interest_order', (db) => {
      db.intOrder[this.meKey(db)] = [...order];
    }, 'personal');
  }

  async postC(entryId: EntryId, body: string): Promise<Comment> {
    await this.lat();
    const k = this.meKey(this.read());
    const created: Comment = { id: newId(), by: k, at: this.now(), t: body };
    this.mutateDb('comments', (db) => {
      db.list = db.list.map((e) =>
        e.entryId === entryId ? { ...e, comments: [...e.comments, created] } : e,
      );
      db.events.push(evt('comment', k, 'game', entryId, this.now()));
    });
    return created;
  }

  // -- media carousel -------------------------------------------------------

  /**
   * Parity with the Supabase path, including the two failure modes the store
   * branches on: `DUPLICATE` for the `(crew, game, provider, key)` unique index
   * and `NO_APPID` for a row the FK could not hold. `ensureGame` stands in for
   * the provisional-catalog step so a video can be attached to a live-search
   * result here too.
   */
  async addGameVideo(
    game: Game,
    v: VideoDraft,
  ): Promise<GameVideo> {
    await this.lat();
    const k = this.meKey(this.read());
    const appid = Number(game.appid);
    if (!Number.isSafeInteger(appid) || appid <= 0) {
      throw new RepoError('NO_APPID', "NO STEAM PAGE — CAN'T ATTACH VIDEOS");
    }
    const created: GameVideo = {
      id: newId(),
      appid,
      provider: v.provider,
      kind: v.kind,
      key: v.key,
      url: v.url,
      title: v.title,
      by: k,
      at: this.now(),
    };
    const cur = this.read();
    if ((cur.videos ?? []).some((x) => x.appid === appid && x.key === v.key)) {
      throw new RepoError('DUPLICATE', 'ALREADY ADDED');
    }
    const known = await this.catalogHas(appid);
    if (!known) {
      await this.ensureGame({
        appid,
        name: game.n,
        dev: game.dev,
        releaseYm: game.rd,
        price: game.p,
        oldPrice: game.o,
        upcoming: game.up,
        description: game.ds,
        artUrl: game.art,
      });
    }
    this.mutateDb('game_videos', (db) => {
      db.videos = [...(db.videos ?? []), created];
      db.events.push(evt('video_add', k, 'game', String(appid), this.now()));
    });
    return created;
  }

  async removeGameVideo(id: string): Promise<void> {
    await this.lat();
    this.mutateDb('game_videos', (db) => {
      db.videos = (db.videos ?? []).filter((v) => v.id !== id);
    });
  }

  private async catalogHas(appid: number): Promise<boolean> {
    return (await this.fetchCatalog()).some((g) => g.appid === appid);
  }

  // -- goty -----------------------------------------------------------------

  async addPot(appid: GameId): Promise<void> {
    await this.lat();
    this.mutateDb('goty_entries', (db) => {
      const k = this.yk(this.meKey(db));
      const cur = db.potentials[k] ?? [];
      if (cur.includes(appid)) throw new RepoError('DUPLICATE', 'ALREADY IN YOUR POTENTIALS');
      db.potentials[k] = [...cur, appid];
    }, 'personal');
  }

  async saveBoards(patch: Partial<MyBoards>): Promise<void> {
    await this.lat();
    this.mutateDb('goty_entries', (db) => {
      const k = this.meKey(db);
      const yk = this.yk(k);
      if (patch.potentials) db.potentials[yk] = [...patch.potentials];
      if (patch.tiers) db.tiers[yk] = { ...patch.tiers };
      if (patch.tierOrder) db.tierOrder[yk] = [...patch.tierOrder];
      if (patch.skips) db.skips[yk] = [...patch.skips];
      if (patch.intOrder) db.intOrder[k] = [...patch.intOrder];
      if (patch.intSkips) { db.intSkips = db.intSkips ?? {}; db.intSkips[k] = [...patch.intSkips]; }
    }, 'personal');
  }

  /**
   * The prototype hardcoded eight appids (line 1790). ARCHITECTURE §3.3 replaces
   * that with a real `steam-sync op=goty` call; LocalRepository keeps the
   * hardcoded list because there is no Steam library to read, and the list is
   * exported from `lib/logic.ts` so the real call drops in unchanged.
   *
   * Names come from the seeded catalog, which is where all eight already live —
   * so the demo build exercises the same "add a named game" path as production
   * without inventing playtime Steam never reported. A private profile is
   * `{profilePublic: false}`, not a throw, matching the wire contract.
   */
  async steamFill(_year: number): Promise<SteamFillResult> {
    await this.lat();
    const db = this.read();
    const link = db.steam[this.meKey(db)];
    if (!link || !link.linked) throw new RepoError('NOT_LINKED');
    if (!link.profilePublic) {
      return {
        profilePublic: false, games: [], dated: false,
        candidates: [], library: [], counts: null,
      };
    }
    const byAppid = new Map(seedCatalog().map((g) => [g.appid, g]));
    const games = GOTY_STEAM_FILL_APPIDS.map((appid) => ({
      appid,
      name: byAppid.get(appid)?.n ?? '',
      playtimeMin: 0,
      lastPlayedAt: 0,
    }));
    // `dated: true` — the demo build takes the PRIMARY path, and that is the
    // honest answer here. The fallback exists because a real third-party Steam
    // account has no last-played timestamps (G-07); fabricating a set of
    // candidates and the "we could not read your history" copy that goes with
    // them would be the class of lie this repository exists not to tell (L-06:
    // offline parity, not a fabricator). The picker is exercised by
    // boards.steamfill.test.ts against a stubbed result instead.
    return {
      profilePublic: true,
      games,
      dated: true,
      candidates: [],
      library: games.map((g) => ({
        appid: g.appid, name: g.name, playtimeMin: g.playtimeMin,
      })),
      counts: null,
    };
  }

  /**
   * Always null, and honestly so: there is no Steam library behind this
   * repository to check, so there has never been a pass. The store reads null as
   * "nothing to wait for" — the same answer a member gets from a function that
   * predates the backfill. The waiting states are exercised in
   * boards.steamfill.test.ts against a stubbed result, like the picker is.
   */
  async fetchSteamBackfill(): Promise<SteamBackfill | null> {
    await this.lat();
    return null;
  }

  // -- what's new -----------------------------------------------------------

  async markReviewed(): Promise<void> {
    await this.lat();
    this.mutateDb('profiles', (db) => {
      const k = this.meKey(db);
      db.users = db.users.map((u) => (u.k === k ? { ...u, lastRev: this.now() } : u));
    });
  }

  async seenUsers(gameIds: GameId[]): Promise<void> {
    await this.lat();
    this.mutateDb('whats_new_seen', (db) => {
      const k = this.meKey(db);
      db.users = db.users.map((u) => {
        if (u.k !== k) return u;
        const s = new Set<GameId>(u.seen);
        for (const id of gameIds) if (id != null) s.add(id);
        return { ...u, seen: [...s] };
      });
    }, 'personal');
  }

  async fetchEvents(sinceIso: string, limit: number): Promise<CrewEvent[]> {
    await this.lat();
    const since = Date.parse(sinceIso) || 0;
    return this.read()
      .events.filter((e) => e.at > since)
      .sort((a, b) => b.at - a.at)
      .slice(0, limit);
  }

  // -- playtests ------------------------------------------------------------

  /**
   * P6 F-2. This repository is offline *parity*, not a fabricator: like every
   * other Steam method here it hits `/api/steam/*` for real (see the class
   * header), so `npm run dev:local` exercises the same store-page parse the
   * Supabase path does. There is no fixture to fall back to — a made-up widget
   * verdict is precisely what INVENTORY §12 item 7 is about — so an unreachable
   * Worker is `PT_CHECK_UNAVAILABLE`, not `widget:false`.
   */
  async ptDoCheck(url: string): Promise<PtCheckResult> {
    await this.lat();
    const db = this.read();
    const catalog = await this.fetchCatalog();
    const base = ptCheckUrl(url, db.pts, (appid) => catalog.find((g) => g.appid === appid) ?? null);
    if (!base.ok) return base;
    try {
      const d = await this.api<PtCheckData>('/api/steam/ptcheck/' + base.appid);
      return ptCheckFound(base, d);
    } catch {
      return { ok: false, err: PT_CHECK_UNAVAILABLE };
    }
  }

  /** P6 F-3 — the honest initial provenance, same rules as the Supabase path. */
  async ptDoTrack(input: PtSteamInput): Promise<Playtest> {
    await this.lat();
    const k = this.meKey(this.read());
    const p = ptTrackFields(input);
    const created: Playtest = {
      id: 'pt' + this.now(),
      kind: 'steam',
      appid: input.appid,
      artUrl: '',
      name: input.name,
      dev: input.dev,
      hue: input.hue,
      party: { mx: input.party, lbl: ptPartyLbl(input.party), vs: false },
      status: p.status,
      ended: false,
      wStart: 0,
      wEnd: 0,
      foot: p.foot,
      auto: false,
      by: k,
      at: this.now(),
      note: p.note,
      desc: '',
      url: input.appid ? 'https://store.steampowered.com/app/' + input.appid + '/' : '',
      srcLabel: '',
      datesSrc: p.datesSrc,
      int: {},
      st: {},
    };
    this.mutateDb('playtests', (db) => {
      db.pts = [...db.pts, created];
      db.events.push(evt('pt_track', k, 'playtest', created.id, this.now()));
    });
    return created;
  }

  async ptDoAddOff(input: PtOffInput): Promise<Playtest> {
    await this.lat();
    let host = '';
    try {
      host = new URL(input.url).hostname.replace(/^www\./, '').toUpperCase();
    } catch {
      throw new RepoError('BAD_URL', 'THAT URL DOES NOT PARSE');
    }
    const k = this.meKey(this.read());
    const created: Playtest = {
      id: 'pt' + this.now(),
      kind: 'off',
      appid: 0,
      artUrl: '',
      name: input.name,
      dev: '—',
      hue: (input.name.length * 47) % 360,
      party: { mx: input.party, lbl: ptPartyLbl(input.party), vs: false },
      status: 'open',
      ended: false,
      wStart: input.start ? Date.parse(input.start + 'T12:00:00Z') : 0,
      wEnd: input.end ? Date.parse(input.end + 'T12:00:00Z') : 0,
      foot: '',
      auto: false,
      by: k,
      at: this.now(),
      note: 'Off-Steam signup — user-maintained, no auto-sync. Anyone can update; every edit is attributed.',
      desc: '',
      url: input.url,
      srcLabel: host,
      datesSrc: 'DATES: USER-ENTERED',
      int: {},
      st: {},
    };
    this.mutateDb('playtests', (db) => {
      db.pts = [...db.pts, created];
      db.events.push(evt('pt_track', k, 'playtest', created.id, this.now()));
    });
    return created;
  }

  async ptToggleInt(playtestId: string): Promise<boolean> {
    await this.lat();
    let on = false;
    this.mutateDb('playtest_interest', (db) => {
      const k = this.meKey(db);
      db.pts = db.pts.map((e) => {
        if (e.id !== playtestId) return e;
        const int = { ...e.int };
        if (int[k]) delete int[k];
        else {
          int[k] = this.now();
          on = true;
        }
        return { ...e, int };
      });
    });
    return on;
  }

  async ptReport(playtestId: string, s: 'in' | 'req'): Promise<void> {
    await this.lat();
    this.mutateDb('playtest_status', (db) => {
      const k = this.meKey(db);
      db.pts = db.pts.map((e) =>
        e.id === playtestId
          ? { ...e, st: { ...e.st, [k]: { s, m: 'SELF-REPORTED ' + ptFmt(this.now()) } } }
          : e,
      );
    });
  }

  async ptClearSt(playtestId: string): Promise<void> {
    await this.lat();
    this.mutateDb('playtest_status', (db) => {
      const k = this.meKey(db);
      db.pts = db.pts.map((e) => {
        if (e.id !== playtestId) return e;
        const st = { ...e.st };
        delete st[k];
        return { ...e, st };
      });
    });
  }

  /**
   * PORTING-NOTES D-04: an `auto` entry is muted for the caller only; anything
   * else is deleted crew-wide. The prototype deleted the row either way while
   * claiming otherwise in the copy.
   */
  async ptUntrack(playtestId: string): Promise<{ muted: boolean }> {
    await this.lat();
    let muted = false;
    this.mutateDb('playtests', (db) => {
      const k = this.meKey(db);
      const e = db.pts.find((x) => x.id === playtestId);
      if (e?.auto) {
        muted = true;
        db.mutes[k] = [...new Set([...(db.mutes[k] ?? []), playtestId])];
      } else {
        db.pts = db.pts.filter((x) => x.id !== playtestId);
      }
    });
    return { muted };
  }

  async ptBrowse(q: string): Promise<PtBrowseRow[]> {
    await this.lat();
    const nq = q.trim().toLowerCase();
    return PT_BROWSE.filter(
      (x) => !nq || x.name.toLowerCase().includes(nq) || x.dev.toLowerCase().includes(nq),
    ).map((x) => ({ ...x, tags: [...x.tags] }));
  }

  // -- steam link -----------------------------------------------------------

  async steamLinkStart(returnTo: string): Promise<{ redirectUrl: string }> {
    await this.lat();
    // No OpenID round trip locally: linking succeeds immediately so the
    // Playtests sync sheet and the GOTY auto-fill are reachable in dev.
    this.mutateDb('steam_links', (db) => {
      const k = this.meKey(db);
      const u = db.users.find((x) => x.k === k);
      db.steam[k] = {
        linked: true,
        profilePublic: true,
        personaName: (u?.name ?? 'PLAYER').toUpperCase().replace(/[^A-Z0-9]/g, '') + '_PLAYS',
        lastSyncAt: this.now(),
        ownedCount: 0,
      };
    });
    return { redirectUrl: returnTo };
  }

  async steamUnlink(): Promise<void> {
    await this.lat();
    this.mutateDb('steam_links', (db) => {
      delete db.steam[this.meKey(db)];
    });
  }

  async steamSyncNow(): Promise<SyncResult> {
    await this.lat();
    const db = this.read();
    const link = db.steam[this.meKey(db)];
    if (!link || !link.linked) {
      return { ok: false, checkedAt: this.now(), profilePublic: false, owned: 0, granted: [], code: 'NOT_LINKED' };
    }
    this.mutateDb('steam_links', (d) => {
      const k = this.meKey(d);
      const cur = d.steam[k];
      if (cur) d.steam[k] = { ...cur, lastSyncAt: this.now() };
    });
    return {
      ok: true,
      checkedAt: this.now(),
      profilePublic: link.profilePublic,
      owned: link.ownedCount,
      granted: [],
    };
  }

  // -- admin ----------------------------------------------------------------

  async doAddUser(i: { name: string; email: string; role: Role }): Promise<Invite> {
    await this.lat();
    const name = i.name.trim();
    if (!name) throw new RepoError('NAME_REQUIRED', 'NAME REQUIRED');
    const cur = this.read();
    const k = deriveUserKey(name, (key) => cur.users.some((u) => u.k === key), this.now());
    const pw = genPw();
    const u: User = {
      k,
      name,
      role: i.role,
      hue: nextHue(cur.users.length),
      col: '',
      email: i.email.trim(),
      // The RETIRED `op=create` path: it minted a temp password and a
      // synthesized login, so the address it collected was only ever the legacy
      // profile note. Nothing calls this any more (docs/INVITES.md §4).
      loginEmail: '',
      mustReset: true,
      av: '',
      lastRev: this.now(),
      seen: [],
    };
    this.mutateDb('profiles', (db) => {
      db.users = [...db.users, u];
      db.creds[k] = pw;
      db.events.push(evt('member_add', db.session, 'profile', k, this.now()));
    });
    return inviteFor(u, pw);
  }

  // == invitations (3026, docs/INVITES.md) ==================================
  //
  // Local mode has no mail, so `inviteMember` always answers with a link and
  // `delivery: 'link'` — the same shape the server returns when it could not
  // send. Everything else is modelled honestly: a pending invite is NOT a
  // membership, it does not appear in the roster, and no account exists until
  // `acceptInvite` is called.

  private invitesFor(crewId: string): LocalInvite[] {
    return this.read().invites.filter((i) => i.crewId === crewId && !i.closedAs);
  }

  async inviteMember(email: string): Promise<InviteSent> {
    await this.lat();
    const addr = email.trim();
    if (!addr || !/^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(addr)) {
      throw new RepoError('EMAIL_INVALID', 'THAT DOES NOT LOOK LIKE AN EMAIL');
    }
    const cur = this.read();
    const wanted = addr.toLowerCase();
    if (cur.users.some((u) => (u.email ?? '').toLowerCase() === wanted)) {
      throw new RepoError('ALREADY_MEMBER', 'THEY ARE ALREADY IN THIS PARTY');
    }

    // Unguessable is not a property local mode needs — there is no network and
    // the store is readable anyway — but it must be UNIQUE, or a second invite
    // shadows the first on lookup and "resend" silently hands back the dead
    // link. NOT derived from the clock: `now` is injectable and the tests
    // freeze it, so two invites in one test minted the same token.
    const token = 'v1_local_' + (++this.inviteSeq).toString(36) + '_' +
      wanted.replace(/[^a-z0-9]/g, '').slice(0, 8);
    const expiresAt = this.now() + INVITE_TTL_DAYS * 86_400_000;
    this.mutateDb('profiles', (db) => {
      // Re-inviting the same address supersedes the old link rather than
      // colliding, exactly as the partial unique index forces server-side.
      for (const inv of db.invites) {
        if (inv.crewId === db.activeCrewId && !inv.closedAs &&
            inv.email.toLowerCase() === wanted) {
          inv.closedAs = 'superseded';
        }
      }
      db.invites = [
        ...db.invites,
        {
          token,
          crewId: db.activeCrewId,
          email: addr,
          invitedBy: db.session,
          createdAt: this.now(),
          expiresAt,
          closedAs: '',
        },
      ];
    });

    return {
      email: addr,
      delivery: 'link',
      expiresAt: new Date(expiresAt).toISOString(),
      link: INVITE_BASE + 'c/' + (this.read().crews.find((c) => c.id === cur.activeCrewId)?.slug ?? '') +
        '/join#invite=' + token,
    };
  }

  async listInvites(): Promise<PendingInvite[]> {
    await this.lat();
    return this.invitesFor(this.read().activeCrewId).map((i) => ({
      email: i.email,
      invitedAt: new Date(i.createdAt).toISOString(),
      expiresAt: new Date(i.expiresAt).toISOString(),
      delivery: 'link' as const,
      sent: false,
    }));
  }

  async revokeInvite(email: string): Promise<void> {
    await this.lat();
    const wanted = email.trim().toLowerCase();
    let found = false;
    this.mutateDb('profiles', (db) => {
      for (const inv of db.invites) {
        if (inv.crewId === db.activeCrewId && !inv.closedAs &&
            inv.email.toLowerCase() === wanted) {
          inv.closedAs = 'revoked';
          found = true;
        }
      }
    });
    if (!found) throw new RepoError('NOT_FOUND', 'NO OPEN INVITATION FOR THAT ADDRESS');
  }

  async peekInvite(token: string): Promise<InvitePeek> {
    await this.lat();
    const cur = this.read();
    const inv = cur.invites.find((i) => i.token === token);
    const crew = inv ? cur.crews.find((c) => c.id === inv.crewId) : undefined;
    if (!inv || !crew) {
      return { state: 'invalid', crewName: '', crewSlug: '', inviterName: '', emailMasked: '', expiresAt: '' };
    }
    const state: InvitePeek['state'] = inv.closedAs
      ? (inv.closedAs as InvitePeek['state'])
      : inv.expiresAt <= this.now()
        ? 'expired'
        : 'live';
    const at = inv.email.indexOf('@');
    return {
      state,
      crewName: crew.name,
      crewSlug: crew.slug,
      inviterName: (cur.users.find((u) => u.k === inv.invitedBy)?.name ?? '').toUpperCase(),
      emailMasked: at > 0 ? inv.email.slice(0, Math.min(2, at)) + '****' + inv.email.slice(at) : '****',
      expiresAt: new Date(inv.expiresAt).toISOString(),
    };
  }

  async acceptInvite(i: {
    token: string;
    displayName?: string;
    password?: string;
  }): Promise<InviteAccepted> {
    await this.lat();
    const cur = this.read();
    const inv = cur.invites.find((x) => x.token === i.token);
    const crew = inv ? cur.crews.find((c) => c.id === inv.crewId) : undefined;
    if (!inv || !crew || inv.closedAs || inv.expiresAt <= this.now()) {
      throw new RepoError('INVITE_CLOSED', 'THAT INVITATION IS NO LONGER OPEN');
    }

    const signedIn = Boolean(cur.session);
    if (!signedIn) {
      const name = (i.displayName ?? '').trim();
      if (!name) throw new RepoError('NAME_REQUIRED', 'CHOOSE A NAME');
      if ((i.password ?? '').length < 8) {
        throw new RepoError('WEAK_PASSWORD', 'USE AT LEAST 8 CHARACTERS');
      }
      const key = deriveUserKey(name, (k) => cur.users.some((u) => u.k === k), this.now());
      this.mutateDb('profiles', (db) => {
        db.users = [
          ...db.users,
          {
            k: key,
            name,
            role: 'user',
            hue: nextHue(db.memberships.filter((m) => m.crewId === inv.crewId).length),
            col: '',
            email: inv.email,
            // An invited account SIGNS IN WITH THE INVITED ADDRESS — that is
            // the whole point of inviting by email, and why nobody who joins
            // this way ever needs seeding.
            loginEmail: inv.email.toLowerCase(),
            // They chose this password. There is nothing to reset.
            mustReset: false,
            av: '',
            lastRev: this.now(),
            seen: [],
          },
        ];
        db.creds[key] = i.password ?? '';
        db.memberships.push({
          crewId: inv.crewId,
          userKey: key,
          role: 'player',
          hue: nextHue(db.memberships.filter((m) => m.crewId === inv.crewId).length),
          col: '',
        });
        db.events.push(evt('member_add', key, 'profile', key, this.now()));
        const row = db.invites.find((x) => x.token === i.token);
        if (row) row.closedAs = 'accepted';
      });
      return { createdAccount: true, crewId: crew.id, crewName: crew.name, userKey: key, email: inv.email };
    }

    const me = cur.session;
    this.mutateDb('profiles', (db) => {
      if (!db.memberships.some((m) => m.crewId === inv.crewId && m.userKey === me)) {
        db.memberships.push({
          crewId: inv.crewId,
          userKey: me,
          role: 'player',
          hue: nextHue(db.memberships.filter((m) => m.crewId === inv.crewId).length),
          col: '',
        });
      }
      db.activeCrewId = inv.crewId;
      db.events.push(evt('member_add', me, 'profile', me, this.now()));
      const row = db.invites.find((x) => x.token === i.token);
      if (row) row.closedAs = 'accepted';
    });
    return { createdAccount: false, crewId: crew.id, crewName: crew.name };
  }

  async declineInvite(token: string): Promise<void> {
    await this.lat();
    this.mutateDb('profiles', (db) => {
      const row = db.invites.find((x) => x.token === token);
      if (row && !row.closedAs) row.closedAs = 'declined';
    });
  }

  // == join links (3056, docs/JOIN-LINKS.md) ================================
  //
  // Modelled honestly on the two things that actually matter: an ASK IS NOT A
  // MEMBERSHIP (a request adds nothing to `memberships`, so it reaches no
  // roster and no count), and the code is a real gate (wrong codes burn
  // attempts and a spent code cannot be spent twice). Local mode has no mail,
  // so the code comes back on `lastJoinCode` instead.

  private liveLink(token: string): LocalJoinLink | undefined {
    return this.read().joinLinks.find((l) => l.token === token && !l.revoked);
  }

  async partyJoinLink(): Promise<JoinLinkState> {
    await this.lat();
    const cur = this.read();
    const row = cur.joinLinks.find((l) => l.crewId === cur.activeCrewId);
    const live = Boolean(row && !row.revoked);
    const slug = cur.crews.find((c) => c.id === cur.activeCrewId)?.slug ?? '';
    return {
      live,
      createdAt: row ? new Date(row.createdAt).toISOString() : null,
      // Re-readable, like the server from 3057.
      joinUrl: live && row ? INVITE_BASE + 'c/' + slug + '/ask#key=' + row.token : null,
    };
  }

  async newPartyJoinLink(): Promise<JoinLinkMinted> {
    await this.lat();
    const cur = this.read();
    const token = 'v1_key_' + (++this.joinSeq).toString(36);
    this.mutateDb('profiles', (db) => {
      // Replace in place: one live link per party, which is a primary key
      // server-side. Rotating therefore KILLS the previous token rather than
      // adding a second — the property the surface has to say out loud.
      db.joinLinks = [
        ...db.joinLinks.filter((l) => l.crewId !== db.activeCrewId),
        { crewId: db.activeCrewId, token, createdAt: this.now(), revoked: false },
      ];
    });
    const slug = cur.crews.find((c) => c.id === cur.activeCrewId)?.slug ?? '';
    return { live: true, joinUrl: INVITE_BASE + 'c/' + slug + '/ask#key=' + token };
  }

  async offPartyJoinLink(): Promise<void> {
    await this.lat();
    this.mutateDb('profiles', (db) => {
      // Open requests are deliberately untouched — they are people who asked
      // while the door was open.
      for (const l of db.joinLinks) {
        if (l.crewId === db.activeCrewId) l.revoked = true;
      }
    });
  }

  async listJoinRequests(): Promise<JoinRequest[]> {
    await this.lat();
    const cur = this.read();
    return cur.joinRequests
      .filter((r) => r.crewId === cur.activeCrewId && !r.closedAs)
      .sort((a, b) => b.requestedAt - a.requestedAt)
      .map((r) => ({
        id: r.id,
        displayName: cur.users.find((u) => u.k === r.userKey)?.name ?? '',
        requestedAt: new Date(r.requestedAt).toISOString(),
      }));
  }

  async decideJoinRequest(id: string, decision: 'approve' | 'deny'): Promise<void> {
    await this.lat();
    const cur = this.read();
    const row = cur.joinRequests.find((r) => r.id === id && !r.closedAs);
    if (!row) throw new RepoError('NOT_FOUND', 'NO OPEN REQUEST WITH THAT ID');

    this.mutateDb('profiles', (db) => {
      const r = db.joinRequests.find((x) => x.id === id && !x.closedAs);
      // Conditional, so a double-tap writes nothing — the local twin of the
      // conditional burn that makes two approvals produce one member.
      if (!r) return;
      r.closedAs = decision === 'approve' ? 'approved' : 'denied';
      if (decision !== 'approve') return;

      const hue = nextHue(db.memberships.filter((m) => m.crewId === r.crewId).length);
      db.memberships.push({
        crewId: r.crewId,
        userKey: r.userKey,
        role: 'player',
        hue,
        col: '',
      });
      // The colour is assigned HERE, not when they asked — two people asking
      // before either is approved would otherwise share one.
      const u = db.users.find((x) => x.k === r.userKey);
      if (u) {
        u.hue = hue;
        // `lastRev` is stamped on approval and not on the ask: they have not
        // missed anything, and stamping it when they asked would present
        // everything the party did while they waited as unread news.
        if (!db.memberships.some((m) => m.userKey === r.userKey && m.crewId !== r.crewId)) {
          u.lastRev = this.now();
        }
      }
      db.events.push(evt('member_add', r.userKey, 'profile', r.userKey, this.now()));
    });
  }

  async peekJoinLink(token: string): Promise<JoinLinkPeek> {
    await this.lat();
    const cur = this.read();
    const link = this.liveLink(token);
    const crew = link ? cur.crews.find((c) => c.id === link.crewId) : undefined;
    if (!link || !crew) {
      return { state: 'invalid', crewName: '', crewSlug: '', leaderName: '', alreadyMember: false };
    }
    const ownerKey = cur.memberships.find(
      (m) => m.crewId === link.crewId && m.role === 'owner',
    )?.userKey;
    return {
      state: 'live',
      crewName: crew.name,
      crewSlug: crew.slug,
      leaderName: (cur.users.find((u) => u.k === ownerKey)?.name ?? '').toUpperCase(),
      alreadyMember: Boolean(
        cur.session &&
          cur.memberships.some((m) => m.crewId === link.crewId && m.userKey === cur.session),
      ),
    };
  }

  async startJoinCode(token: string, email: string): Promise<JoinCodeSent> {
    await this.lat();
    const link = this.liveLink(token);
    if (!link) throw new RepoError('LINK_CLOSED', "THAT JOIN LINK ISN'T VALID");
    const addr = email.trim();
    if (!addr || !/^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(addr)) {
      throw new RepoError('EMAIL_INVALID', 'THAT DOES NOT LOOK LIKE AN EMAIL');
    }
    const cur = this.read();
    const wanted = addr.toLowerCase();

    /* THE ACCOUNT CHECK IS FIRST, before any code is minted (Marcus,
       2026-08-30: "that should be the first thing checked when entering an
       email"). It reverses an earlier anti-oracle choice — see the note in
       `join-request`'s opStart — and it is what stops the most likely real
       visitor, an existing member following a friend's link, from spending a
       code only to be told at the end that they should have logged in.
       Both stores, like `assertEmailFree` server-side. */
    if (
      cur.users.some(
        // `authEmailOf`, NOT `u.loginEmail`: a seeded account carries an empty
        // loginEmail and really signs in at `<key>@crew.quarterdown.com`, so
        // comparing the raw column misses the whole legacy population — the
        // exact half `assertEmailFree` checks two stores to catch.
        (u) => authEmailOf(u).toLowerCase() === wanted ||
          (u.email ?? '').toLowerCase() === wanted,
      )
    ) {
      throw new RepoError('EMAIL_TAKEN', 'YOU ALREADY HAVE AN ACCOUNT ON THIS EMAIL.');
    }

    const id = 'ch_' + (++this.joinSeq).toString(36);
    const code = String((this.joinSeq * 137_911) % 1_000_000).padStart(6, '0');
    const expiresAt = this.now() + 15 * 60_000;
    this.mutateDb('profiles', (db) => {
      // Asking again supersedes rather than colliding, exactly as the partial
      // unique index forces server-side.
      for (const c of db.joinChallenges) {
        if (c.crewId === link.crewId && !c.consumed && c.email.toLowerCase() === wanted) {
          c.consumed = true;
        }
      }
      db.joinChallenges = [
        ...db.joinChallenges,
        {
          id,
          crewId: link.crewId,
          email: addr,
          code,
          attempts: 0,
          createdAt: this.now(),
          expiresAt,
          consumed: false,
        },
      ];
    });
    this.lastJoinCode = code;

    const at = addr.indexOf('@');
    return {
      emailMasked: addr.slice(0, Math.min(2, at)) + '****' + addr.slice(at),
      expiresAt: new Date(expiresAt).toISOString(),
      expiresMinutes: 15,
    };
  }

  async verifyJoinCode(i: {
    token: string;
    email: string;
    code: string;
    displayName: string;
    password: string;
  }): Promise<JoinAsked> {
    await this.lat();
    const cur = this.read();
    const link = this.liveLink(i.token);
    const crew = link ? cur.crews.find((c) => c.id === link.crewId) : undefined;
    if (!link || !crew) throw new RepoError('LINK_CLOSED', "THAT JOIN LINK ISN'T VALID");

    const wanted = i.email.trim().toLowerCase();
    const ch = cur.joinChallenges.find(
      (c) => c.crewId === link.crewId && !c.consumed && c.email.toLowerCase() === wanted,
    );
    if (!ch) throw new RepoError('CODE_INVALID', 'THAT CODE IS NOT RIGHT');
    if (ch.expiresAt <= this.now()) {
      throw new RepoError('CODE_EXPIRED', 'THAT CODE HAS EXPIRED');
    }
    if (ch.attempts >= 5) {
      throw new RepoError('CODE_LOCKED', 'TOO MANY TRIES. ASK FOR A NEW CODE.');
    }
    if ((i.code ?? '').replace(/\s+/g, '') !== ch.code) {
      // The increment IS the control, so it lands before the throw.
      this.mutateDb('profiles', (db) => {
        const row = db.joinChallenges.find((c) => c.id === ch.id);
        if (row) row.attempts += 1;
      });
      const left = Math.max(0, 5 - (ch.attempts + 1));
      throw left > 0
        ? new RepoError('CODE_INVALID', 'THAT CODE IS NOT RIGHT')
        : new RepoError('CODE_LOCKED', 'TOO MANY TRIES. ASK FOR A NEW CODE.');
    }

    // Checked after the code matches but BEFORE it is spent: caught here it
    // costs a retype, caught after it would cost the whole code.
    const name = (i.displayName ?? '').trim();
    if (!name) throw new RepoError('NAME_REQUIRED', 'CHOOSE A NAME');
    if ((i.password ?? '').length < 8) {
      throw new RepoError('WEAK_PASSWORD', 'USE AT LEAST 8 CHARACTERS');
    }
    if (cur.users.some((u) => (u.loginEmail ?? '').toLowerCase() === wanted)) {
      throw new RepoError('EMAIL_TAKEN', 'YOU ALREADY HAVE AN ACCOUNT ON THIS EMAIL.');
    }

    const key = deriveUserKey(name, (k) => cur.users.some((u) => u.k === k), this.now());
    const id = 'jr_' + (++this.joinSeq).toString(36);
    this.mutateDb('profiles', (db) => {
      const row = db.joinChallenges.find((c) => c.id === ch.id);
      if (row) row.consumed = true;
      db.users = [
        ...db.users,
        {
          k: key,
          name,
          role: 'user',
          // NO COLOUR YET — it is assigned on approval, so two people asking
          // before either is let in do not share one.
          hue: 150,
          col: '',
          email: ch.email,
          loginEmail: wanted,
          mustReset: false,
          av: '',
          lastRev: this.now(),
          seen: [],
        },
      ];
      db.creds[key] = i.password ?? '';
      // NO MEMBERSHIP. This is the whole point: an ask is not a join.
      db.joinRequests = [
        ...db.joinRequests,
        { id, crewId: link.crewId, userKey: key, requestedAt: this.now(), closedAs: '' },
      ];
    });

    return { createdAccount: true, crewName: crew.name, email: ch.email };
  }

  async requestJoin(token: string): Promise<JoinAsked> {
    await this.lat();
    const cur = this.read();
    const link = this.liveLink(token);
    const crew = link ? cur.crews.find((c) => c.id === link.crewId) : undefined;
    if (!link || !crew) throw new RepoError('LINK_CLOSED', "THAT JOIN LINK ISN'T VALID");
    if (!cur.session) throw new RepoError('UNAUTHORIZED', 'SIGN IN FIRST');
    if (cur.memberships.some((m) => m.crewId === link.crewId && m.userKey === cur.session)) {
      throw new RepoError('ALREADY_MEMBER', 'YOU ARE ALREADY IN THAT PARTY');
    }
    if (
      cur.joinRequests.some(
        (r) => r.crewId === link.crewId && r.userKey === cur.session && !r.closedAs,
      )
    ) {
      throw new RepoError('REQUEST_OPEN', 'YOU HAVE ALREADY ASKED TO JOIN');
    }

    /* NO CODE. Existing accounts are verified by decision (§3.3) — adding an
       address test here is the obvious "fix" for somebody who finds the gap
       later, and it is not one. */
    const id = 'jr_' + (++this.joinSeq).toString(36);
    this.mutateDb('profiles', (db) => {
      db.joinRequests = [
        ...db.joinRequests,
        { id, crewId: link.crewId, userKey: db.session, requestedAt: this.now(), closedAs: '' },
      ];
    });
    return { createdAccount: false, crewName: crew.name };
  }

  async joinRequestStatus(): Promise<JoinStatus> {
    await this.lat();
    const cur = this.read();
    const none = { waiting: false, crewName: '', crewSlug: '', leaderName: '', requestedAt: '' };
    if (!cur.session) throw new RepoError('UNAUTHORIZED', 'SIGN IN FIRST');
    const row = cur.joinRequests
      .filter((r) => r.userKey === cur.session && !r.closedAs)
      .sort((a, b) => b.requestedAt - a.requestedAt)[0];
    if (!row) return none;
    const crew = cur.crews.find((c) => c.id === row.crewId);
    // The party was deleted while they waited. Answer honestly rather than
    // claiming a denial for something that no longer exists.
    if (!crew) return none;
    const ownerKey = cur.memberships.find(
      (m) => m.crewId === row.crewId && m.role === 'owner',
    )?.userKey;
    return {
      waiting: true,
      crewName: crew.name,
      crewSlug: crew.slug,
      leaderName: (cur.users.find((u) => u.k === ownerKey)?.name ?? '').toUpperCase(),
      requestedAt: new Date(row.requestedAt).toISOString(),
    };
  }

  async withdrawJoinRequest(): Promise<void> {
    await this.lat();
    this.mutateDb('profiles', (db) => {
      for (const r of db.joinRequests) {
        if (r.userKey === db.session && !r.closedAs) r.closedAs = 'withdrawn';
      }
    });
  }

  async doResetPw(userKey: string): Promise<Invite> {
    await this.lat();
    const u = this.read().users.find((x) => x.k === userKey);
    if (!u) throw new RepoError('NOT_FOUND');
    const pw = genPw();
    this.mutateDb('profiles', (db) => {
      db.creds[userKey] = pw;
      db.users = db.users.map((x) => (x.k === userKey ? { ...x, mustReset: true } : x));
    });
    return inviteFor(u, pw);
  }


  async renameCrew(crewId: string, name: string): Promise<string> {
    await this.lat();
    const db = this.read();
    const next = name.trim();
    // The same three refusals 3030 makes, so local mode cannot succeed where
    // production would not.
    if (!next) throw new RepoError('BAD_REQUEST', 'crew name is required');
    if (next.length > 40) throw new RepoError('BAD_REQUEST', 'crew name is too long');
    if (
      !db.memberships.some(
        (m) => m.crewId === crewId && m.userKey === db.session && m.role === 'owner',
      )
    ) {
      throw new RepoError('FORBIDDEN', 'not the owner of that crew');
    }
    this.mutateDb('crews', (d) => {
      // THE SLUG IS NOT TOUCHED — see `Repository.renameCrew`. Re-deriving it
      // here would let local mode pass a test that production must fail.
      d.crews = d.crews.map((c) => (c.id === crewId ? { ...c, name: next } : c));
    });
    return next;
  }

  async transferOwnership(userKey: string): Promise<void> {
    await this.lat();
    const db = this.read();
    const target = db.users.find((x) => x.k === userKey);
    if (!target) throw new RepoError('NOT_FOUND', 'No such member');
    if (userKey === db.session) throw new RepoError('BAD_REQUEST', 'You already own this crew');
    // The same three refusals 3020 makes, so local mode cannot succeed where
    // production would not.
    if (!db.memberships.some((m) => m.crewId === db.activeCrewId && m.userKey === db.session && m.role === 'owner')) {
      throw new RepoError('FORBIDDEN', 'Only the crew owner can transfer ownership');
    }
    if (!db.memberships.some((m) => m.crewId === db.activeCrewId && m.userKey === userKey)) {
      throw new RepoError('FORBIDDEN', 'That account is not a member of this party');
    }
    if (target.mustReset) {
      throw new RepoError('FORBIDDEN', 'That member has not finished their first sign-in yet');
    }

    this.mutateDb('crew_members', (d) => {
      d.memberships = d.memberships.map((m) =>
        m.crewId !== d.activeCrewId
          ? m
          : m.userKey === d.session
            ? { ...m, role: 'player' as const }
            : m.userKey === userKey
              ? { ...m, role: 'owner' as const }
              : m,
      );
      // The legacy column follows, exactly as migration 3023 makes it follow
      // server-side.
      d.users = d.users.map((u) =>
        u.k === d.session
          ? { ...u, role: 'user' as const }
          : u.k === userKey
            ? { ...u, role: 'admin' as const }
            : u,
      );
    });
  }

  async doRm(userKey: string): Promise<void> {
    await this.lat();
    this.mutateDb('profiles', (db) => {
      if (userKey === db.session) throw new RepoError('SELF_REMOVE');
      db.users = db.users.filter((u) => u.k !== userKey);
      delete db.creds[userKey];
      // Attribution nulls rather than cascading — renders as FORMER FRIEND.
      db.list = db.list.map((e) => (e.by === userKey ? { ...e, by: '' } : e));
      db.events.push(evt('member_remove', db.session, 'profile', userKey, this.now()));
    });
  }

  // -- steam facts ----------------------------------------------------------

  private api<T>(path: string, init?: RequestInit): Promise<T> {
    return steamApi<T>({ apiBase: this.apiBase, fetchImpl: this.fetchImpl }, path, init);
  }

  /**
   * `GET /api/steam/search` returns `{total, items}` where `items` are raw
   * storesearch rows — the Worker deliberately does not rewrite them
   * (worker/src/steam.ts `mapSearch`), so the row → `Game` mapping stays here,
   * byte for byte as the prototype had it at line 1639.
   */
  async fetchLive(q: string, signal?: AbortSignal): Promise<LiveSearchResult> {
    const query = q.trim();
    if (query.length < 2) return { items: [], src: 'steam', fromNetwork: true };
    try {
      const data = await this.api<{ total: number; items: unknown[] }>(
        '/api/steam/search?q=' + encodeURIComponent(query),
        signal ? { signal } : undefined,
      );
      const cat = seedCatalog();
      const ids = new Set(cat.map((g) => g.id));
      const norms = new Set(cat.map((g) => normName(g.n)));
      const items = data.items
        .map(toLiveGame)
        .filter((g): g is Game => !!g && !ids.has(g.id) && !norms.has(normName(g.n)))
        .slice(0, 12);
      return { items, src: 'steam', fromNetwork: true };
    } catch {
      return { items: fixtureLiveFor(query), src: 'steam', fromNetwork: false };
    }
  }

  /**
   * Dev parity for Steam's search. Hits the real route when the Worker is up,
   * and falls back to a fixture so `npm run dev:local` can still exercise the
   * browse UI with no backend at all — the same contract `fetchLive` has.
   */
  /**
   * The dev / offline double for the ranked feed, and — because there is no Deno
   * test runner and the only Supabase test is gated on `QD_LIVE=1` — the ONLY
   * part of this feature CI can execute end to end. Every store test builds a
   * real LocalRepository, so the feed slice is tested through this.
   *
   * It ranks with the SAME functions the Phase 1 fallback uses (`pass`,
   * `passFlt`, `sortArr`), which is what makes it a fair double: if the two
   * paths disagree about a filter, a test here can catch it.
   *
   * It reports `reason: 'no_taste'` and `personalized: false`, and that is the
   * truth rather than a stub — there is no `steam_owned_apps` locally, so there
   * is no taste profile to rank against and no ownership to caption with. A
   * local double that claimed `personalized: true` would make the one state
   * §5.6 most wants visible impossible to see in development.
   *
   * The cursor is an OPAQUE OFFSET here versus a keyset on the server. Both are
   * opaque to the caller, which is the property that matters; the client must
   * never parse either.
   */
  async fetchDiscoverPage(q: DiscoverQuery, _signal?: AbortSignal): Promise<DiscoverPageResult> {
    await this.lat();
    const db = this.read();
    const pool = [...seedCatalog(), ...db.extraGames.map((g) => ({ ...g }))];
    for (const g of pool) applyFact(g, db.facts[String(g.appid)]);
    // discover_page decorates its row JSON with the same Steam display fields
    // and ccu_at stamp games_bootstrap does — parity here keeps the seam honest.
    for (const g of pool) {
      applySteamDisplay(g);
      applyCcuAt(g, this.now());
    }

    const matched = pool.filter((g) => pass(g, [], q.q));
    // OWNED BY, server-side — migration 3047's predicate as executable client
    // code, exactly as `passXTags` below is `xtags`'. It matters here and not
    // just for tidiness: the FEED is the one path that never re-filters on the
    // client, so if the double did not apply this the demo build would offer
    // the section on Discover and ignore it, which is the failure the one-panel
    // pass spent a day removing. Same derivation as `fetchCrewOwnedBy` — the
    // two are the same stand-in and must not drift.
    //
    // SEEDED COMPLETE, which the store also does and for the same reason:
    // `passFlt` treats a missing entry as UNKNOWN and passes it, so handing it
    // an owners-only map would make every unowned row survive the filter. The
    // map the predicate needs is "asked about all of these", and only a caller
    // that knows what it asked can build it.
    const owners: Record<number, string[]> = {};
    if (q.flt.own.length) {
      const owned = await this.fetchCrewOwnedBy(pool.map((g) => g.appid));
      for (const g of pool) owners[g.appid] = owned[g.appid] ?? [];
    }
    // `passXTags` beside `passFlt`, exactly as `discover_page` applies `xtags`
    // beside its filter set — and inside `hiddenByFilters`' subtraction, so the
    // `hiddenByFilters + total === unfiltered total` contract keeps holding
    // with excludes active.
    const filtered = matched.filter(
      (g) => passFlt(g, q.flt, db.pts, owners) && passXTags(g, q.excludes),
    );
    const ordered = sortArr(filtered, q.sort, q.q);

    const from = cursorOffset(q.cursor);
    const limit = Math.max(1, Math.min(q.limit, 100));
    const rows = ordered.slice(from, from + limit).map((g) => ({ ...g }));
    const end = from + rows.length;

    const counts = new Map<string, number>();
    for (const g of filtered) for (const t of g.tags) counts.set(t, (counts.get(t) ?? 0) + 1);

    return {
      rows,
      assets: {},
      total: filtered.length,
      hiddenByFilters: matched.length - filtered.length,
      poolN: pool.length,
      tagCounts: [...counts.entries()]
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .slice(0, 60)
        .map(([id, n]) => ({ id, label: id.toUpperCase(), n })),
      nextCursor: end < ordered.length ? offsetCursor(end) : null,
      limitUsed: limit,
      personalized: false,
      reason: pool.length === 0 ? 'empty_table' : 'no_taste',
      seed: q.seed,
      fromNetwork: false,
    };
  }

  async fetchBrowse(q: BrowseQuery, signal?: AbortSignal): Promise<BrowsePageResult> {
    if (q.term === '' && q.tags.length === 0) {
      return { total: 0, items: [], assets: {}, start: q.start, found: 0, capped: false, fromNetwork: false };
    }
    const p = browseParams(q);

    try {
      const data = await this.api<{
        total: number;
        rows: unknown[];
        start: number;
        found: number;
        capped: boolean;
      }>('/api/steam/browse?' + p.toString(), signal ? { signal } : undefined);

      const items: Game[] = [];
      const assets: AssetMap = {};
      for (const raw of data.rows) {
        const mapped = toBrowseGame(raw);
        if (!mapped) continue;
        items.push(mapped.game);
        if (mapped.lib !== '' || mapped.hdr !== '') {
          assets[String(mapped.game.appid)] = { lib: mapped.lib, hdr: mapped.hdr };
        }
        // Same registration the Supabase path makes, for the same reason: this
        // is where `ensureGame` reads a browse row's real name from when it is
        // added to the list. `extraGames` is LocalRepository's `liveGames`.
        if (!this.read().extraGames.some((g: Game) => g.appid === mapped.game.appid)) {
          this.mutateDb('catalog_games', (d) => {
            d.extraGames.push({ ...mapped.game });
          });
        }
      }
      return {
        total: data.total,
        items,
        assets,
        start: data.start,
        found: data.found,
        capped: data.capped === true,
        fromNetwork: true,
      };
    } catch {
      const items = fixtureBrowseFor(q);
      return {
        total: items.length,
        items,
        assets: FIXTURE_ASSETS,
        start: q.start,
        found: items.length,
        capped: false,
        fromNetwork: false,
      };
    }
  }

  /** Dev parity for the tag picker's vocabulary. */
  /**
   * A deterministic fixture series for demo mode, flagged `sample: true`.
   *
   * This is where the old chart's Lehmer PRNG went. It used to run in
   * production, seeded off the appid and scaled to the curated all-time peak,
   * with a `SAMPLE` chip as the only thing distinguishing it from a
   * measurement. The generator is reproduced bit-for-bit — `Math.imul(48271, x)
   * & 2147483647` is deliberately NOT MINSTD, and masking is not the same as
   * `% (2^31-1)` — because demo screenshots should keep the shape people
   * recognise. What changed is where it is allowed to run: here only, behind a
   * flag the view-model turns into a visible chip.
   *
   * Games with no curated peak get no series, matching the old gate: a
   * fabricated chart for a row the prototype never claimed one for would be a
   * new lie rather than a preserved one.
   */
  async fetchCcuSeries(appid: number): Promise<{ points: CcuSample[]; sample: boolean }> {
    const g = (await this.fetchCatalog()).find((x) => x.appid === appid || x.id === appid);
    const peak = g?.pk ?? 0;
    if (!g || !peak || g.up) return { points: [], sample: true };

    let x = (appid || 7) % 2147483647 || 7;
    const r = (): number => (x = Math.imul(48271, x) & 2147483647) / 2147483647;
    const today = Math.floor(this.now() / 86_400_000);
    const points: CcuSample[] = [];
    let v = 0.45 + r() * 0.3;
    for (let i = CHART_DAYS - 1; i >= 0; i--) {
      v = Math.min(1, Math.max(0.12, v + (r() - 0.48) * 0.16));
      points.push({ at: (today - i) * 86_400_000, ccu: Math.round(v * peak) });
    }
    return { points, sample: true };
  }

  /**
   * Demo parity for migration 3045 — a fabricated price year, labelled as one.
   *
   * `sample: true` on every path, exactly as `fetchCcuSeries` above does, and
   * that flag is the whole safety property: `SupabaseRepository` cannot set it,
   * so a chart drawn from invention can only ever appear in demo mode. The same
   * PRNG (`Math.imul(48271, x)`, deliberately not MINSTD) so demo screenshots
   * stay stable between runs.
   *
   * Shaped like a real price history rather than noise: a flat list price with
   * occasional sale windows cut into it. A random walk would produce a price
   * that drifts, which no game's price does, and the sparkline would teach a
   * reader to expect the wrong shape.
   */
  async fetchPriceSeries(appid: number): Promise<{ points: PriceSample[]; sample: boolean }> {
    const g = (await this.fetchCatalog()).find((x) => x.appid === appid || x.id === appid);
    const list = g?.p ?? 0;
    // No price, free, or unreleased: nothing to draw, and the caller's empty
    // state says which — same three-way split the real path produces.
    if (!g || !list || g.up) return { points: [], sample: true };

    let x = (appid || 11) % 2147483647 || 11;
    const r = (): number => (x = Math.imul(48271, x) & 2147483647) / 2147483647;
    const today = Math.floor(this.now() / 86_400_000);
    const points: PriceSample[] = [];

    // Walk the year forward, opening a sale roughly every ~7 weeks and holding
    // it for 7-14 days at 25-70% off — Steam's seasonal cadence, near enough
    // for a demo.
    let saleLeft = 0;
    let salePrice = list;
    for (let i = PRICE_CHART_DAYS - 1; i >= 0; i--) {
      if (saleLeft > 0) {
        saleLeft -= 1;
      } else if (r() < 0.02) {
        saleLeft = 7 + Math.floor(r() * 8);
        salePrice = Math.max(0.99, Math.round(list * (0.3 + r() * 0.45) * 100) / 100);
      }
      points.push({ at: (today - i) * 86_400_000, price: saleLeft > 0 ? salePrice : list });
    }
    return { points, sample: true };
  }

  /**
   * Demo parity for migration 3048.
   *
   * Answers as though the walk HAS run — `patchSeenAt` is always set — because
   * the interesting demo states are the two real ones: a game that patched
   * recently, and a game that has announcements but has not patched in years
   * (Divinity: Original Sin 2's actual state). "Never checked" is a transient
   * the demo has no way to leave, so parking every row in it would show the
   * least informative of the three states permanently.
   *
   * Derived from the appid rather than random so a demo screenshot is stable.
   */
  async fetchDetailFacts(appid: number): Promise<DetailFacts | null> {
    const g = (await this.fetchCatalog()).find((x) => x.appid === appid || x.id === appid);
    if (!g) return null;
    const seen = new Date(this.now()).toISOString();
    // The 30-day half. A third of rows are QUIET — no reviews in the window —
    // because that is the honest majority state for a back catalogue and it is
    // the one a reader is most likely to mistake for a broken job. `rev30Score`
    // tracks the percentage loosely rather than being derived from it: the
    // whole point of the column is that Steam's rung folds in the review count.
    const quiet = appid % 3 === 0;
    const pct = quiet ? null : 60 + (appid % 40);
    const rev = {
      rev30Pct: pct,
      rev30Score: pct === null ? null : pct >= 95 ? 9 : pct >= 80 ? 8 : pct >= 70 ? 6 : 5,
      rev30Ct: quiet ? 0 : 5 + (appid % 900),
      rev30At: seen,
    };
    // Unreleased games have nothing to have patched.
    if (g.up) return { patchAt: null, patchTitle: null, patchSeenAt: seen, ...rev };
    // Roughly a fifth of rows read as "has not patched in living memory",
    // which is the state the real catalog is full of and the one a reader is
    // most likely to think is a bug.
    if (appid % 5 === 0) return { patchAt: null, patchTitle: null, patchSeenAt: seen, ...rev };
    const daysAgo = 3 + (appid % 900);
    return {
      patchAt: new Date(this.now() - daysAgo * 86_400_000).toISOString(),
      patchTitle: 'Update ' + (1 + (appid % 40)),
      patchSeenAt: seen,
      ...rev,
    };
  }

  /**
   * Demo parity for migration 3007.
   *
   * This repository has no owned-apps table — `db.steam` records only who has
   * linked. So `linked` is real and `owned` is DERIVED: a linked member is
   * treated as owning a game that is on the crew's list, which is the
   * believable case (you added it because somebody has it) and is honest about
   * being a stand-in rather than inventing a per-member library.
   *
   * The `linked`/`owned` split is preserved exactly, because the roster's
   * `NO STEAM LINK` state has to be reachable in demo mode too — it is the
   * state most likely to be got wrong, and a demo that never shows it is a
   * demo that never tests it.
   */
  async fetchCrewOwnership(appid: number): Promise<CrewOwnershipRow[]> {
    await this.lat();
    const db = this.read();
    const onList = db.list.some((e) => Number(e.id) === appid);
    return db.users.map((u) => {
      const linked = !!db.steam[u.k];
      return { userKey: u.k, linked, owned: linked && onList };
    });
  }

  /**
   * Demo parity for migration 3046.
   *
   * DERIVED THE SAME WAY `fetchCrewOwnership` derives its `owned`, and it has
   * to be the identical rule: the two answer the same question at different
   * widths, and a demo where the Detail roster says `✓ IN LIBRARY` while the
   * OWNED BY filter hides the row would be a bug that only exists in the
   * double. So a linked member owns exactly the games on the crew's list.
   *
   * OWNERS ONLY, and an appid nobody owns is ABSENT from the map rather than
   * `[]` — the store seeds the requested ids, so the two still read as "asked",
   * and the shape stays what `crew_games_ownership` actually returns.
   *
   * One knock-on worth naming: in demo mode every linked member owns every
   * listed game, so an OWNED BY selection over the boards narrows to the list
   * and nothing further. That is the stand-in being honest about being one, not
   * the filter failing — the same trade `fetchCrewOwnership` already made.
   */
  async fetchCrewOwnedBy(appids: readonly number[]): Promise<Record<number, string[]>> {
    await this.lat();
    const db = this.read();
    const linked = db.users.filter((u) => !!db.steam[u.k]).map((u) => u.k);
    const onList = new Set(db.list.map((e) => Number(e.id)));
    const out: Record<number, string[]> = {};
    if (linked.length === 0) return out;
    for (const a of new Set(appids)) if (onList.has(a)) out[a] = [...linked];
    return out;
  }

  /**
   * Demo parity for the BACKLOG's ownership tabs.
   *
   * SAME STAND-IN AS `fetchCrewOwnedBy` — a linked member owns exactly the
   * games on the crew's list — because the two answer the same question about
   * overlapping sets, and a demo where OWNED BY and the OWNED tab disagreed
   * about one game would be a bug that exists only in the double. Unlinked, or
   * a game off the list, is UNOWNED here.
   *
   * PLAYTIME IS DERIVED, NOT INVENTED, and the distinction is the one
   * `steamFill` above draws for itself: there is no Steam library to read
   * locally, so a made-up minute count would be this repository fabricating the
   * very fact the tab filters on. What it can say honestly is which games the
   * fixture records a SCORE for — the demo's own evidence that the member
   * played something — and those get a nominal hour. Everything else is owned
   * with `0`, which is a real state (owned, never launched) rather than a
   * guess. So PLAYED in demo mode means "scored", and it is the fixture being
   * a fixture, not the tab meaning something different in production.
   */
  async fetchMyLibrary(appids: readonly number[]): Promise<Record<number, number>> {
    await this.lat();
    const db = this.read();
    const k = this.meKey(db);
    const out: Record<number, number> = {};
    if (!db.steam[k]) return out;
    const onList = new Set(db.list.map((e) => Number(e.id)));
    const scored = new Set(Object.keys(db.scores?.[k] ?? {}).map((id) => Number(id)));
    for (const a of new Set(appids)) {
      if (!onList.has(a)) continue;
      out[a] = scored.has(a) ? 60 : 0;
    }
    return out;
  }

  /**
   * Demo parity for migration 3010.
   *
   * `db.steam` is exactly the demo's record of who has linked, so unlike
   * `fetchCrewOwnership`'s `owned` this needs no stand-in — it is the same fact
   * the fixture already holds. The seed leaves some members unlinked on purpose:
   * a demo where every card reads `STEAM LINKED` would not exercise the state
   * that was wrong for the whole of P2-08's life.
   */
  async fetchCrewSteamLinks(): Promise<CrewLinkRow[]> {
    await this.lat();
    const db = this.read();
    return db.users.map((u) => ({ userKey: u.k, linked: !!db.steam[u.k] }));
  }

  /**
   * Demo-mode overrides, in memory rather than in `LocalDb`.
   *
   * The fixture catalog is rebuilt from `seedCatalog()` on every read, so a
   * persisted count here would drift from the row it is supposed to be
   * overriding the moment the seed changed. Losing them on reload is the right
   * trade for a fixture: what demo mode has to exercise is the round trip and
   * every refusal, not durability.
   */
  private readonly povr = new Map<number, PlayerOverride>();

  async fetchPlayerOverride(appid: number): Promise<PlayerOverride | null> {
    await this.lat();
    return this.povr.get(appid) ?? null;
  }

  async setPlayerCount(
    appid: number,
    min: number | null,
    max: number | null,
  ): Promise<ResolvedPlayers | null> {
    await this.lat();
    const db = this.read();
    // The same gates in the same order as `set_player_count` (2906), so demo
    // mode reaches every toast the live path can produce rather than only the
    // happy one.
    if (!db.session) throw new Error('NOT_AUTHENTICATED');
    const seed = seedCatalog().find((g) => g.appid === appid);
    if (!appid || !seed) throw new Error('UNKNOWN_GAME');

    if (min === null && max === null) {
      this.povr.delete(appid);
      // Clearing falls back to whatever the fixture says, which is exactly the
      // rung the live resolver would drop to.
      return { mn: seed.mn, mx: seed.mx, punb: seed.punb === true, ...(seed.psrc ? { psrc: seed.psrc } : null) };
    }
    if (min === null || max === null) throw new Error('BAD_RANGE');
    if (max < 1 || max > 1000) throw new Error('BAD_RANGE');
    if (min < 1 || min > max) throw new Error('BAD_RANGE');

    this.povr.set(appid, {
      min,
      max,
      at: this.now(),
      byKey: db.session,
      byKnown: true,
    });
    return { mn: min, mx: max, punb: false, psrc: 'override' };
  }

  /**
   * Dev parity for the live-once seat lookup: the canned answers a real
   * `player-lookup` would give for the fixture browse's thin rows, so a
   * `VITE_REPOSITORY=local` browse shows counts popping onto tiles the same
   * beat production does. Unknown appids answer nothing, exactly like a game
   * IGDB has never heard of.
   */
  async lookupSeats(appids: number[]): Promise<SeatCount[]> {
    await this.lat();
    return appids
      .map((a) => FIXTURE_SEATS[String(a)])
      .filter((c): c is SeatCount => c !== undefined);
  }

  async fetchTagList(): Promise<SteamTag[]> {
    try {
      const data = await this.api<{ tags: SteamTag[]; hash: string }>('/api/steam/tags');
      return Array.isArray(data.tags) && data.tags.length > 0 ? data.tags : FIXTURE_TAGS;
    } catch {
      return FIXTURE_TAGS;
    }
  }

  /** P6 F-1 — see `Repository.fetchCsRows`. No fixture: an outage is an outage. */
  async fetchCsRows(q: string, signal?: AbortSignal): Promise<unknown[]> {
    const d = await this.api<{ rows: unknown[] }>(
      '/api/cs/search?q=' + encodeURIComponent(q.trim()),
      signal ? { signal } : undefined,
    );
    return Array.isArray(d.rows) ? d.rows : [];
  }

  /**
   * `GET /api/steam/app/:appid` returns the raw appdetails `data` object. Only
   * the *volatile* set is extracted — the curated editorial fields are never
   * written from a Steam response (ARCHITECTURE §1.4 / decision D2, and the
   * prototype's `light` branch at line 1671).
   */
  async enrich(appid: number, _light: boolean): Promise<EnrichResult> {
    try {
      const d = await this.api<Record<string, unknown>>('/api/steam/app/' + appid);
      return { patch: mapAppDetails(appid, d), media: mapAppMedia(d) };
    } catch {
      // There is no media fixture and there should not be one: an invented
      // trailer URL is the kind of fabrication INVENTORY §12 is about. The
      // carousel simply falls back to its crew videos and its empty state.
      return { patch: FIXTURE_FACTS[String(appid)] ?? { appid }, media: emptyMedia() };
    }
  }

  /**
   * The response records carry the ribbon flags as well as the art
   * (`types.ts#AssetRec`), so `store/slices/steamFacts.ts#revalidate` can put
   * UPCOMING / EARLY ACCESS / DEMO on a live search row without opening it.
   */
  async giFlush(appids: number[]): Promise<AssetsResult> {
    const ids = [...new Set(appids)].filter((a) => a > 0).slice(0, 50).sort((a, b) => a - b);
    if (!ids.length) return { assets: {}, fromNetwork: true };
    try {
      const assets = await this.api<AssetMap>('/api/steam/items', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ appids: ids }),
      });
      // "An empty result counts as failure" — ARCHITECTURE §4.3 EMPTY, and the
      // prototype's `throw new Error('empty')` at line 1533.
      if (!Object.keys(assets).length) throw new RepoError('EMPTY');
      return { assets: this.withLocalFlags(ids, assets), fromNetwork: true };
    } catch {
      const assets: AssetMap = {};
      for (const a of ids) {
        const rec = FIXTURE_ASSETS[String(a)];
        if (rec) assets[String(a)] = { ...rec };
      }
      return { assets: this.withLocalFlags(ids, assets), fromNetwork: false };
    }
  }

  /**
   * Local stand-in for what GetItems answers in production: the ribbon flags of
   * every requested appid this store already knows a fact for. Without it the
   * `--local` dev mode is the one place where a live search row can never get a
   * banner, which is exactly the bug this batching path exists to fix.
   *
   * An appid with facts but no art still gets a record (`lib`/`hdr` empty):
   * `logic.ts#artCands` skips empty rungs, so a flag-only record costs nothing
   * and is the only way the flags reach the store.
   *
   * `FIXTURE_FACTS` backs a store that has not saved a fact yet, the same way
   * `enrich()` above falls back to it — a fresh `--local` boot would otherwise
   * have an empty `db.facts` and show no banners at all.
   */
  private withLocalFlags(ids: readonly number[], assets: AssetMap): AssetMap {
    const facts = this.read().facts;
    for (const a of ids) {
      const f = facts[String(a)] ?? FIXTURE_FACTS[String(a)];
      if (!f) continue;
      const rec = assets[String(a)] ?? { lib: '', hdr: '' };
      if (f.up !== undefined) rec.up = f.up;
      if (f.ea !== undefined) rec.ea = f.ea;
      // `demo: false` is never emitted upstream either — see `AssetRec`.
      if (f.demo) rec.demo = true;
      if (f.dAid !== undefined) rec.dAid = f.dAid;
      assets[String(a)] = rec;
    }
    return assets;
  }

  async saveFix(patches: SteamFactPatch[]): Promise<void> {
    if (!patches.length) return;
    this.mutateDb('steam_facts', (db) => {
      for (const p of patches) {
        db.facts[String(p.appid)] = { ...db.facts[String(p.appid)], ...p, appid: p.appid };
      }
      for (const p of patches) {
        const g = db.extraGames.find((x) => x.appid === p.appid);
        if (g) applyFact(g, p);
      }
    });
  }

  // -- rounds (migration 3039) ---------------------------------------------
  //
  // The same rules the RPCs enforce, refused with the same words, so local
  // mode cannot succeed where production would not. Two-tab parity comes for
  // free: every write is a `mutateDb('rounds', …)` and the other tab's
  // `subscribeCrew` handler refetches `roundGet()`.

  /** The open round, applying the idle rule — 3 h without activity is over. */
  private openRound(db: LocalDb): RoundSnapshot | null {
    const r = db.round;
    if (!r) return null;
    if (this.now() - r.lastActivityAt > 3 * 3600 * 1000) return null;
    return r;
  }

  private roundOf(db: LocalDb): RoundSnapshot {
    const r = this.openRound(db);
    if (!r) throw new RepoError('NOT_FOUND', 'no game night open');
    return r;
  }

  private roundMember(db: LocalDb): string {
    const k = this.meKey(db);
    if (!db.memberships.some((m) => m.crewId === db.activeCrewId && m.userKey === k)) {
      throw new RepoError('FORBIDDEN', 'not a crew member');
    }
    return k;
  }

  private static roundShut(r: RoundSnapshot): boolean {
    return r.lock || r.spin !== null;
  }

  private roundLanded(r: RoundSnapshot): boolean {
    return !!r.spin && r.spin.startedAt + r.spin.ms <= this.now();
  }

  /**
   * `round_geom()`: the slice table in draw order, with the ticket weights.
   *
   * The tier roster is `r.joined` — whoever is SEATED tonight, not the crew
   * (2026-08-28, mirroring `public.round_seat_tier()`). Reading it off the
   * snapshot rather than off `memberships` is what makes a join or a leave
   * re-weigh the next draw with nothing else to keep in step.
   *
   * ONE ROW PER GAME (3054): `noms` is one row per quarter, so seconded games
   * are folded here exactly as the SQL groups them — one wide wedge whose
   * weight carries the seconds, never two thin ones with the same title.
   */
  private roundGeom(db: LocalDb, r: RoundSnapshot): RoundGeomRow[] {
    const seatKeys = new Set(r.joined);
    const users = db.users.filter((u) => seatKeys.has(u.k));
    return groupNoms(r.noms).map((h) => {
      const e = db.list.find((x) => x.id === h.id);
      const tier = rTier(e?.votes, users);
      return { appid: h.id, by: h.bys[0] ?? '', bys: h.bys, tier, w: ticketsFor(tier, r.wtd, h.bys.length) };
    });
  }

  /** `round_after_hat_change()`: a result never outlives its nomination. */
  private static roundSetHat(r: RoundSnapshot, keep: RoundSnapshot['noms']): RoundSnapshot {
    const next = { ...r, noms: keep };
    if (next.spin && !keep.some((n) => n.id === next.spin?.winner)) {
      next.spin = null;
      next.lock = false;
    }
    return next;
  }

  private putRound(fn: (db: LocalDb, r: RoundSnapshot) => RoundSnapshot | null): RoundSnapshot | null {
    const db = this.mutateDb('rounds', (d) => {
      const r = this.roundOf(d);
      const next = fn(d, r);
      d.round = next ? { ...next, lastActivityAt: this.now() } : null;
    });
    return db.round;
  }

  async serverNow(): Promise<number> {
    return this.now();
  }

  async roundGet(): Promise<RoundSnapshot | null> {
    await this.lat();
    return this.openRound(this.read());
  }

  async roundStart(): Promise<RoundSnapshot | null> {
    await this.lat();
    const db = this.read();
    const k = this.roundMember(db);
    const live = this.openRound(db);
    if (live) return live;
    const now = this.now();
    const r: RoundSnapshot = {
      id: newId(),
      crewId: db.activeCrewId,
      startedBy: k,
      by: k,
      startedAt: now,
      at: now,
      lastActivityAt: now,
      wtd: true,
      lock: false,
      schedNight: null,
      joined: [k],
      noms: [],
      spin: null,
    };
    this.mutateDb('rounds', (d) => {
      d.round = r;
      d.events.push(evt('round_start', k, 'round', r.id, now));
    });
    return r;
  }

  async roundJoin(): Promise<RoundSnapshot | null> {
    await this.lat();
    const k = this.roundMember(this.read());
    return this.putRound((_db, r) => {
      if (r.lock) throw new RepoError('FORBIDDEN', 'game night is locked');
      return r.joined.includes(k) ? r : { ...r, joined: [...r.joined, k] };
    });
  }

  async roundLeave(): Promise<RoundSnapshot | null> {
    await this.lat();
    const k = this.roundMember(this.read());
    return this.putRound((_db, r) => {
      const joined = r.joined.filter((x) => x !== k);
      if (!joined.length) return null; // last one out closes the round
      let next: RoundSnapshot = { ...r, joined };
      if (!LocalRepository.roundShut(r)) {
        next = LocalRepository.roundSetHat(next, r.noms.filter((n) => n.by !== k));
      }
      if (r.by === k) {
        // the lever never leaves with its holder; an in-flight spin is halted
        next = { ...next, by: joined[0] ?? '' };
        if (next.spin && !this.roundLanded(next)) next = { ...next, spin: null, lock: false };
      }
      return next;
    });
  }

  /**
   * `round_kick()`: the leader takes a seat back (2026-08-28). Same shape as
   * LEAVE minus the handover — the leader is the caller, so the lever stays
   * put and the round can never be emptied this way.
   */
  async roundKick(userKey: string): Promise<RoundSnapshot | null> {
    await this.lat();
    const db = this.read();
    const k = this.roundMember(db);
    return this.putRound((d, r) => {
      if (r.by !== k) throw new RepoError('FORBIDDEN', "game night's host only");
      if (userKey === k) throw new RepoError('FORBIDDEN', 'leave, do not kick yourself');
      if (!r.joined.includes(userKey)) throw new RepoError('FORBIDDEN', 'they are not in game night');
      let next: RoundSnapshot = { ...r, joined: r.joined.filter((x) => x !== userKey) };
      // Their quarters come back while the rail is open, as LEAVE does; once
      // the lever is pulled every nomination stays where it is.
      if (!LocalRepository.roundShut(r)) {
        next = LocalRepository.roundSetHat(next, r.noms.filter((n) => n.by !== userKey));
      }
      d.events.push(
        evt('round_kick', k, 'round', r.id, this.now(), {
          display_name: d.users.find((u) => u.k === userKey)?.name ?? '',
        }),
      );
      return next;
    });
  }

  async roundNominate(appid: number): Promise<RoundSnapshot | null> {
    await this.lat();
    const k = this.roundMember(this.read());
    return this.putRound((db, r) => {
      if (!r.joined.includes(k)) throw new RepoError('FORBIDDEN', 'join to nominate');
      if (LocalRepository.roundShut(r)) throw new RepoError('FORBIDDEN', 'wheel has been spun');
      if (!db.list.some((e) => e.id === appid)) throw new RepoError('FORBIDDEN', 'not on the crew list');
      // 3054: the toggle is on MY quarter alone. Somebody else's stays where
      // it is, so a game I take back does not leave the wheel — it narrows.
      if (r.noms.some((n) => n.id === appid && n.by === k)) {
        return LocalRepository.roundSetHat(r, r.noms.filter((n) => !(n.id === appid && n.by === k)));
      }
      if (r.noms.filter((n) => n.by === k).length >= R_QUOTA) {
        throw new RepoError('FORBIDDEN', 'no quarters left');
      }
      return { ...r, noms: [...r.noms, { id: appid, by: k, at: this.now() }] };
    });
  }

  async roundClearMine(): Promise<RoundSnapshot | null> {
    await this.lat();
    const k = this.roundMember(this.read());
    return this.putRound((_db, r) => {
      if (LocalRepository.roundShut(r)) throw new RepoError('FORBIDDEN', 'wheel has been spun');
      return LocalRepository.roundSetHat(r, r.noms.filter((n) => n.by !== k));
    });
  }

  async roundSetWeighted(weighted: boolean): Promise<RoundSnapshot | null> {
    await this.lat();
    const k = this.roundMember(this.read());
    return this.putRound((_db, r) => {
      if (r.by !== k) throw new RepoError('FORBIDDEN', "game night's host only");
      if (LocalRepository.roundShut(r)) throw new RepoError('FORBIDDEN', 'wheel has been spun');
      return { ...r, wtd: weighted };
    });
  }

  /**
   * THE DRAW, local edition — the same arithmetic as `round_spin()`, with
   * `Math.random()` standing in for Postgres's. Production never runs this.
   */
  async roundSpin(): Promise<RoundSnapshot | null> {
    await this.lat();
    const k = this.roundMember(this.read());
    return this.putRound((db, r) => {
      if (r.by !== k) throw new RepoError('FORBIDDEN', "game night's host only");
      if (r.lock) throw new RepoError('FORBIDDEN', 'locked in');
      if (r.spin && !this.roundLanded(r)) throw new RepoError('FORBIDDEN', 'already spinning');
      const geom = this.roundGeom(db, r);
      if (!geom.length) throw new RepoError('FORBIDDEN', 'nothing to spin');
      const slices = rGeom(geom.map((g) => ({ id: g.appid, w: g.w })));
      const total = geom.reduce((s, g) => s + g.w, 0);
      let roll = Math.random() * total;
      let wi = geom.length - 1;
      for (let i = 0; i < geom.length; i++) {
        roll -= geom[i]?.w ?? 0;
        if (roll < 0) {
          wi = i;
          break;
        }
      }
      const sl = slices[wi];
      const mid = sl?.mid ?? 0;
      const span = sl?.span ?? 360;
      const jit = (Math.random() - 0.5) * span * 0.6;
      const cur = r.spin?.to ?? 0;
      const need = ((((-(mid + jit) - cur) % 360) + 360) % 360 + 360) % 360;
      return {
        ...r,
        lock: false,
        spin: {
          seq: (r.spin?.seq ?? 0) + 1,
          from: cur,
          to: cur + 1440 + need,
          startedAt: this.now() + 600,
          ms: R_SPIN_MS,
          winner: geom[wi]?.appid ?? 0,
          geom,
        },
      };
    });
  }

  async roundLock(): Promise<RoundSnapshot | null> {
    await this.lat();
    const k = this.roundMember(this.read());
    return this.putRound((db, r) => {
      if (r.by !== k) throw new RepoError('FORBIDDEN', "game night's host only");
      if (!r.spin || !this.roundLanded(r)) throw new RepoError('FORBIDDEN', 'nothing has landed');
      db.events.push(evt('round_lock', k, 'round', r.id, this.now()));
      return { ...r, lock: true };
    });
  }

  async roundUnlock(): Promise<RoundSnapshot | null> {
    await this.lat();
    const k = this.roundMember(this.read());
    return this.putRound((_db, r) => {
      if (r.by !== k) throw new RepoError('FORBIDDEN', "game night's host only");
      if (!r.lock) throw new RepoError('FORBIDDEN', 'not locked');
      return { ...r, lock: false };
    });
  }

  async roundUndoSpin(): Promise<RoundSnapshot | null> {
    await this.lat();
    const k = this.roundMember(this.read());
    return this.putRound((_db, r) => {
      if (r.by !== k) throw new RepoError('FORBIDDEN', "game night's host only");
      if (!r.spin) throw new RepoError('FORBIDDEN', 'nothing to undo');
      return { ...r, spin: null, lock: false };
    });
  }

  async roundReset(): Promise<RoundSnapshot | null> {
    await this.lat();
    const k = this.roundMember(this.read());
    return this.putRound((_db, r) => {
      if (r.by !== k) throw new RepoError('FORBIDDEN', "game night's host only");
      return { ...r, noms: [], spin: null, lock: false, at: this.now() };
    });
  }

  async roundEnd(): Promise<void> {
    await this.lat();
    const db = this.read();
    const k = this.roundMember(db);
    const owner = db.memberships.some((m) => m.crewId === db.activeCrewId && m.userKey === k && m.role === 'owner');
    this.putRound((d, r) => {
      if (r.by !== k && !owner) throw new RepoError('FORBIDDEN', 'host or party leader only');
      d.events.push(evt('round_end', k, 'round', r.id, this.now()));
      return null;
    });
  }

  // -- SCHEDULE (3071) -------------------------------------------------------
  //
  // The rules `schedule_*()` enforce, against `db.sched`: crew-list games
  // only, once per night, the nominator's quarter stays on, only the
  // nominator edits the window or removes it. Every write is a
  // `mutateDb('schedule_noms', …)`, so the other tab refetches.

  /** `schedule_check_night()`: not over (a day's slack), not absurdly far out. */
  private schedOpenNight(night: string): void {
    const today = new Date(this.now());
    const d = scParse(night);
    const days = Math.round((d.getTime() - new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime()) / 86400000);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(night) || days < -1) throw new RepoError('FORBIDDEN', 'this night has passed');
    if (days > 400) throw new RepoError('FORBIDDEN', 'too far out');
  }

  private static schedWindowOk(from: number | null, to: number | null): void {
    if (from != null && to != null && from >= to) throw new RepoError('FORBIDDEN', 'empty window');
  }

  private putNight(night: string, fn: (noms: SchedNom[], k: string, db: LocalDb) => SchedNom[]): SchedNom[] {
    let out: SchedNom[] = [];
    this.mutateDb('schedule_noms', (d) => {
      const k = this.roundMember(d);
      out = fn((d.sched ?? {})[night]?.slice() ?? [], k, d);
      const sched = { ...(d.sched ?? {}) };
      if (out.length) sched[night] = out;
      else delete sched[night];
      d.sched = sched;
    });
    return out;
  }

  async schedGet(from: string, to: string): Promise<SchedMap> {
    await this.lat();
    const db = this.read();
    this.roundMember(db);
    const out: SchedMap = {};
    for (const [k, v] of Object.entries(db.sched ?? {})) if (k >= from && k <= to && v.length) out[k] = v;
    return out;
  }

  async schedNominate(night: string, appid: number, from: number | null, to: number | null): Promise<SchedNom[]> {
    await this.lat();
    this.schedOpenNight(night);
    LocalRepository.schedWindowOk(from, to);
    return this.putNight(night, (noms, k, db) => {
      if (!db.list.some((e) => e.id === appid)) throw new RepoError('FORBIDDEN', 'not on the party list');
      if (noms.some((n) => n.id === appid)) throw new RepoError('FORBIDDEN', 'already nominated for this night');
      return [...noms, { id: appid, by: k, at: this.now(), from, to, bys: [k] }];
    });
  }

  async schedBack(night: string, appid: number): Promise<SchedNom[]> {
    await this.lat();
    this.schedOpenNight(night);
    return this.putNight(night, (noms, k) => {
      const n = noms.find((x) => x.id === appid);
      if (!n) throw new RepoError('NOT_FOUND', 'not nominated for this night');
      if (n.by === k) throw new RepoError('FORBIDDEN', 'your own nomination keeps your quarter');
      const bys = n.bys.includes(k) ? n.bys.filter((b) => b !== k) : [...n.bys, k];
      return noms.map((x) => (x.id === appid ? { ...x, bys } : x));
    });
  }

  async schedSetRange(night: string, appid: number, from: number | null, to: number | null): Promise<SchedNom[]> {
    await this.lat();
    this.schedOpenNight(night);
    LocalRepository.schedWindowOk(from, to);
    return this.putNight(night, (noms, k) => {
      if (!noms.some((x) => x.id === appid && x.by === k)) throw new RepoError('FORBIDDEN', 'only the nominator can change the window');
      return noms.map((x) => (x.id === appid ? { ...x, from, to } : x));
    });
  }

  async schedRemove(night: string, appid: number): Promise<SchedNom[]> {
    await this.lat();
    return this.putNight(night, (noms, k) => {
      if (!noms.some((x) => x.id === appid && x.by === k)) throw new RepoError('FORBIDDEN', 'only the nominator can remove it');
      return noms.filter((x) => x.id !== appid);
    });
  }

  /**
   * `schedule_start_night()`: the ordinary round, opened with the listed
   * nominations in the hat — one quarter per backer, R_QUOTA each in the order
   * they backed — every backer seated, and the night remembered.
   */
  async schedStart(night: string, appids: number[]): Promise<RoundSnapshot | null> {
    await this.lat();
    const db = this.read();
    const k = this.roundMember(db);
    const live = this.openRound(db);
    if (live) return live;
    const now = this.now();
    const members = new Set(db.memberships.filter((m) => m.crewId === db.activeCrewId).map((m) => m.userKey));
    const noms = (db.sched?.[night] ?? []).filter((n) => appids.includes(n.id) && db.list.some((e) => e.id === n.id));
    if (!noms.length) throw new RepoError('FORBIDDEN', 'nothing crosses the line yet');
    const joined = [k];
    const spent: Record<string, number> = {};
    const hat: RoundSnapshot['noms'] = [];
    noms.forEach((n) => {
      n.bys.forEach((b, i) => {
        if (!members.has(b)) return;
        if (!joined.includes(b)) joined.push(b);
        if ((spent[b] ?? 0) >= R_QUOTA) return;
        spent[b] = (spent[b] ?? 0) + 1;
        hat.push({ id: n.id, by: b, at: n.at + i + 1 });
      });
    });
    // Nothing reached the hat: refuse, as the server does, rather than open an
    // empty wheel in the party's name.
    if (!hat.length) throw new RepoError('FORBIDDEN', 'nothing crosses the line yet');
    const r: RoundSnapshot = {
      id: newId(),
      crewId: db.activeCrewId,
      startedBy: k,
      by: k,
      startedAt: now,
      at: now,
      lastActivityAt: now,
      wtd: true,
      lock: false,
      schedNight: night,
      joined,
      noms: hat.sort((a, b) => a.at - b.at || a.id - b.id),
      spin: null,
    };
    this.mutateDb('rounds', (d) => {
      d.round = r;
      d.events.push(evt('round_start', k, 'round', r.id, now));
    });
    return r;
  }

  // -- realtime -------------------------------------------------------------

  subscribeCrew(_crewId: string, h: CrewHandlers): Unsub {
    this.crewHandlers.add(h);
    h.onStatus?.('subscribed');
    return () => this.crewHandlers.delete(h);
  }

  subscribePersonal(_userId: string, h: PersonalHandlers): Unsub {
    this.personalHandlers.add(h);
    return () => this.personalHandlers.delete(h);
  }
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function structuredCopy<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

function clonePt(p: Playtest): Playtest {
  return { ...p, party: { ...p.party }, int: { ...p.int }, st: { ...p.st } };
}

let idSeq = 0;

/** Comment/event id. PORTING-NOTES D-05: uuid, not `<gameId>-<Date.now()>`. */
function newId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* not available */
  }
  idSeq += 1;
  return 'id_' + Date.now().toString(36) + '_' + idSeq;
}

function evt(
  kind: string,
  actorKey: string,
  subjectType: string,
  subjectId: string,
  at: number,
  payload: CrewEvent['payload'] = {},
): CrewEvent {
  return { id: newId(), kind, actorKey, subjectType, subjectId, payload, at };
}

/** The volatile-fact merge; curated fields are never touched (D2 / §1.4). */
/**
 * The local feed's cursor. An OFFSET, where the server's is a `<score>|<appid>`
 * keyset — different mechanics, same contract: opaque to the caller, and the
 * caller must never parse either. `off:` prefixed so a local cursor accidentally
 * sent to the server decodes to something obviously not a keyset rather than to
 * a plausible-looking score.
 */
function offsetCursor(n: number): string {
  return btoa('off:' + String(n));
}

function cursorOffset(c: string | null): number {
  if (!c) return 0;
  try {
    const t = atob(c);
    const n = t.startsWith('off:') ? Number(t.slice(4)) : NaN;
    return Number.isSafeInteger(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

function applyFact(g: Game, f: SteamFactPatch | undefined): void {
  if (!f) return;
  if (f.p !== undefined) g.p = f.p;
  if (f.o !== undefined) g.o = f.o;
  if (f.up !== undefined) g.up = f.up;
  if (f.ea !== undefined) g.ea = f.ea;
  if (f.demo !== undefined) g.demo = f.demo;
  if (f.dAid !== undefined) g.dAid = f.dAid;
  if (f.rd !== undefined) g.rd = f.rd;
  // The second copy of `applyFact` — this one mutates in place and lives 1400
  // lines from the slice's version. Nothing makes them agree but a human, so a
  // field added to one and missed here renders in production and not in demo.
  if (f.rdFull !== undefined) g.rdFull = f.rdFull;
  if (f.rp !== undefined) g.rp = f.rp;
  if (f.rs !== undefined) g.rs = f.rs;
}

/**
 * `normName`, `toLiveGame` and `mapAppDetails` moved to `repo/steamApi.ts` in
 * Phase 4 so `SupabaseRepository` shares one copy (PORTING-NOTES P4-01).
 */
